import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../App';
import { DollarSign, Package, Truck, ChevronDown, ChevronUp, AlertCircle, Mail, X, FileText, Info, CheckSquare, MapPin } from 'lucide-react';
import { validateParcelsForFax } from '../utils/parcelValidation';
import { Button } from './ui/button';
import { resolveCollectorDefaults } from '../utils/taxCollectorsApi';
import { computeDelinquentGroups } from '../lib/paDelinquentGrouping';

// Shipping method options
const SHIPPING_METHODS = [
  { value: 'FedEx', label: 'FedEx' },
  { value: 'FedEx Low Priority', label: 'FedEx Low Priority' },
  { value: 'UPS', label: 'UPS' },
  { value: 'USPS', label: 'USPS' },
  { value: 'Courier Pickup', label: 'Courier Pickup' },
  { value: 'Client Account', label: 'Client Account' },
  { value: 'Other', label: 'Other' }
];

// Feb 2026 — human-readable label for a tax_collectors.collector_type enum value.
// Used by the "Sending to" recipient banner / strip to confirm to the processor
// who the Mail-Away request is actually going to (e.g. MBM Collections vs.
// the County Treasurer's office) before the request is created.
const prettyCollectorType = (t) => {
  if (!t) return null;
  const map = {
    government:           'Government',
    third_party:          'Third-party',
    third_party_agency:   'Third-party',
    municipal:            'Municipal',
    municipal_collector:  'Municipal',
    county_treasurer:     'County Treasurer',
    school_collector:     'School District',
    school_district:      'School District',
    private_collector:    'Private Collector',
  };
  return map[t] || t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * MailAwayModal Component
 * 
 * Modal for creating mail-away requests with:
 * - Per-jurisdiction MLC fees (from jurisdiction directory)
 * - Per-jurisdiction shipping methods and fees
 * - Per-jurisdiction SASE requirement (from jurisdiction directory, read-only)
 * 
 * Shipping Priority (per jurisdiction):
 * 1. User's current selection
 * 2. Jurisdiction-specific defaults (from jurisdictions collection)
 * 3. Client-level defaults (from client code)
 * 4. Blank (requires manual selection)
 * 
 * SASE: Read from jurisdiction directory, displayed per jurisdiction (read-only)
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSubmit - Callback when form is submitted with mail-away data
 * @param {object} orderData - Order details (order_number, property, borrower_name, etc.)
 * @param {object} certificateData - Tax certificate data (optional)
 * @param {object} clientCodeConfig - Client code configuration with shipping defaults (optional)
 */
const MailAwayModal = ({ isOpen, onClose, onSubmit, orderData, certificateData = null, clientCodeConfig = null, targetJurisdiction = null, preselectUnsentOnly = false }) => {
  const { user } = useAuth();
  
  // Determine if user can edit (only Admin/Team Leader can edit)
  const isAdmin = user?.role === 'admin';
  const isTeamLeader = user?.role === 'tax_team_leader' || user?.role === 'title_team_leader';
  const canEdit = isAdmin || isTeamLeader;
  const isLocked = !canEdit;
  
  // State for jurisdictions with their shipping data
  const [jurisdictions, setJurisdictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ──────────────────────────────────────────────────────────────────
  // PA Delinquent-Collector Grouping (Feb 2026) — Slice 2 wiring.
  //
  // When the order is a PA order AND ≥2 selected jurisdictions point
  // to the same delinquent_collector_id with at least one delinquent
  // installment, surface the [Combine] / [Send separately] banner
  // above the submit button. Default is Combine (operator approval
  // locked during design review).
  //
  // The grouping plan is recomputed against `jurisdictions` (the
  // multi-jurisdiction list the modal already loaded) + the order's
  // tax_certificate snapshot via `certificateData`. We pass `orderData`
  // merged with `certificateData` to the helper so the eligibility
  // predicate can read installments + delinquent_collector_id which
  // live on the cert snapshot, not the order doc.
  // ──────────────────────────────────────────────────────────────────
  const [paCombineSelected, setPaCombineSelected] = useState(true);

  const _paGroupingPlan = useMemo(() => {
    if (!orderData) return null;
    const allIds = (jurisdictions || [])
      .map((j) => j?.jurisdiction_id || j?.id)
      .filter(Boolean);
    if (allIds.length < 2) return null;
    // Merge certificate data into the order shape the helper expects.
    const orderForGrouping = {
      ...orderData,
      tax_certificate: certificateData || orderData?.tax_certificate || null,
    };
    return computeDelinquentGroups(orderForGrouping, allIds);
  }, [orderData, certificateData, jurisdictions]);
  const _paGroups = _paGroupingPlan?.groups || null;

  // ──────────────────────────────────────────────────────────────────
  // Feb 2026 — Unified Mail-Away selection. For each jurisdiction
  // loaded into the modal, compute which request kinds (Current-Tax,
  // Delinquent Tax Claim Bureau) are AVAILABLE so the checkbox panel
  // can render them with the correct disabled/enabled state.
  //
  //   • Current-Tax MLC  → always available. Recipient is the
  //                         current-tax collector configured on the
  //                         cert, falling back to the jurisdiction
  //                         itself when no separate collector exists.
  //   • Delinquent MLC   → only available when BOTH conditions hold:
  //                         (1) `same_as_current_payee` (or
  //                             `delinquent_same_as_current`) is False
  //                         (2) `delinquent_collector_id` is configured
  //                         When ineligible, render the checkbox
  //                         visible-but-disabled with a plain-English
  //                         reason so the operator understands why.
  // ──────────────────────────────────────────────────────────────────
  const kindEligibility = useMemo(() => {
    const cert = certificateData || orderData?.tax_certificate || {};
    const certIndex = {};        // by jurisdiction_id
    const certByName = {};       // by lowercased name
    const ingest = (j) => {
      if (!j) return;
      const id = j.jurisdiction_id || j.id;
      if (id) certIndex[String(id)] = j;
      const nm = (j.jurisdiction_name || j.name || '').toLowerCase().trim();
      if (nm) certByName[nm] = j;
    };
    (cert?.parcels || []).forEach((p) => (p?.jurisdictions || []).forEach(ingest));
    (cert?.jurisdictions || []).forEach(ingest);
    (orderData?.parcels || []).forEach((p) => (p?.jurisdictions || []).forEach(ingest));
    (orderData?.jurisdictions || []).forEach(ingest);

    const map = {};
    (jurisdictions || []).forEach((jur) => {
      const certJur =
        certIndex[String(jur.id)]
        || certByName[(jur.name || '').toLowerCase().trim()]
        || {};
      const sameRaw = certJur.same_as_current_payee;
      const sameFallback = certJur.delinquent_same_as_current;
      const sameAsCurrent =
        (sameRaw === undefined || sameRaw === null) ? sameFallback : sameRaw;
      const dcId = certJur.delinquent_collector_id || jur.delinquent_collector_id || null;
      // Jul 2026 (MAILAWAY-DLQ-NAME-001) — prefer the backend-RESOLVED
      // master-directory collector name carried on the jurisdiction row
      // (`jur.delinquent_collector_name`, resolved from tax_collectors
      // by the /mailaway/requests endpoint — e.g. "PORTNOFF LAW
      // ASSOCIATES"). This is the SAME resolution the individual
      // Mail-Away creation flow uses, so the bulk dialog's selection
      // list / summary / Sending-To now match the created request.
      // Cert snapshot fields remain the fallback; the generic
      // "Tax Claim Bureau" label is the last resort only.
      const backendResolvedName =
        (jur.delinquent_collector_id
          && (!dcId || String(jur.delinquent_collector_id) === String(dcId)))
          ? jur.delinquent_collector_name
          : null;
      const dcName =
        backendResolvedName
        || certJur.delinquent_collector_name
        || certJur.delinquent_payee_name
        || (dcId ? 'Tax Claim Bureau' : null);

      let delinquentEligible = false;
      let delinquentReason = null;
      if (sameAsCurrent === true) {
        delinquentReason = 'Same as Current Payee is enabled';
      } else if (!dcId) {
        delinquentReason = 'No delinquent collector configured';
      } else {
        delinquentEligible = true;
      }

      // Jun 2026 (MLC-LINKED-PAYEE) — payee-first: mirror the
      // Fulfillment Contact Info priority (make_checks_payable_to
      // before the generic collector directory display name) so the
      // review screen, PDF and Fulfillment all show the same name.
      const currentRecipientName =
        certJur.current_make_checks_payable_to
        || certJur.make_checks_payable_to
        || certJur.current_collector_name
        || certJur.collector_name
        || jur.name;

      // Feb 2026 — Current-Tax Collector Grouping (all-states).
      // Surface the resolved current-tax collector id so the
      // pendingRequests builder below can bucket multiple
      // jurisdictions sharing the same collector into a single
      // Combined Current-Tax row (mirroring the Delinquent grouping
      // on the Tax Claim Bureau side). Accepts both the canonical
      // `current_collector_id` and the legacy `tax_collector_id`
      // alias so older cert snapshots still group correctly.
      const currentCollectorId =
        certJur.current_collector_id
        || certJur.tax_collector_id
        || null;

      // Feb 2026 (UAT-PAYEE-GROUPING) — Payee fingerprint.
      // Real-world data has TWO different collector records sharing
      // the same "Make Checks Payable To" + address (e.g. borough
      // collector + school collector both remitting to the same
      // municipal treasurer). The operator's intent is one envelope
      // per PAYEE, not per collector record. We compute a stable
      // normalized fingerprint `(MCPT, address)` here so the
      // pendingRequests builder can bucket two different collector
      // ids into ONE preview row when they resolve to the same payee.
      //
      // Sources, in priority order:
      //   1. `current_make_checks_payable_to` (cert form override)
      //   2. `make_checks_payable_to` (cert snapshot denormalized)
      //   3. `current_collector_name`     (fallback to display name)
      // Address sources:
      //   1. `current_collector_mailing_address`
      //   2. `current_mailing_address`
      //   3. (none — fingerprint still produced from MCPT alone)
      //
      // Returns null when no MCPT is resolvable — caller falls back
      // to `currentCollectorId` as the bucket key.
      const _mcpt = (
        certJur.current_make_checks_payable_to
        || certJur.make_checks_payable_to
        || certJur.current_collector_name
        || ''
      );
      const _addr = (
        certJur.current_collector_mailing_address
        || certJur.current_mailing_address
        || certJur.current_collector_address
        || ''
      );
      // Feb 2026 (UAT-HOMOGLYPHS) — Cyrillic look-alikes (e.g. U+0422
      // Cyrillic Т vs Latin T) appear in seed/operator-entered data
      // and would otherwise produce DIFFERENT fingerprints for what
      // operators perceive as the same payee. Apply NFKC + a narrow
      // Cyrillic→Latin map before the existing case/whitespace
      // normalization. Mirrors `_normalize_for_fingerprint` in
      // /app/backend/services/current_tax_grouping.py — keep both
      // sides in sync.
      const _CYR_HOMOGLYPHS = {
        'А':'A','В':'B','С':'C','Е':'E','Н':'H','К':'K',
        'М':'M','О':'O','Р':'P','Т':'T','Х':'X','Ѕ':'S',
        'І':'I','Ј':'J','Ү':'Y',
        'а':'a','е':'e','о':'o','р':'p','с':'c','х':'x','у':'y',
      };
      const _normFp = (raw) => {
        let s = String(raw || '').normalize('NFKC');
        let out = '';
        for (const ch of s) out += _CYR_HOMOGLYPHS[ch] || ch;
        return out.trim().toUpperCase().replace(/\s+/g, ' ');
      };
      const _mcptNorm = _normFp(_mcpt);
      const _addrNorm = _normFp(_addr);
      const currentRecipientFingerprint = _mcptNorm
        ? `payee::${_mcptNorm}::addr::${_addrNorm}`
        : null;

      map[jur.id] = {
        currentRecipientName,
        currentCollectorId,
        currentRecipientFingerprint,
        delinquentEligible,
        delinquentReason,
        delinquentCollectorId: dcId,
        delinquentCollectorName: dcName,
      };
    });
    return map;
  }, [jurisdictions, certificateData, orderData]);

  // Per-jurisdiction request-kind selection state.
  // Shape: { [jurId]: { current: bool, delinquent: bool } }
  // Defaults: current=true (always opted in), delinquent=eligibility
  // (auto-opted-in when available so operators don't have to discover it).
  const [requestKindSel, setRequestKindSel] = useState({});

  useEffect(() => {
    if (jurisdictions.length === 0) {
      setRequestKindSel({});
      return;
    }
    setRequestKindSel((prev) => {
      const next = {};
      let changed = false;
      jurisdictions.forEach((jur) => {
        const elig = kindEligibility[jur.id] || {};
        const existing = prev[jur.id];
        if (existing) {
          // Preserve operator choice. But if eligibility flipped to
          // false (e.g. operator toggled "Same as Current Payee" mid-
          // session and re-opened the modal), force delinquent off.
          next[jur.id] = {
            current: existing.current,
            delinquent: existing.delinquent && elig.delinquentEligible,
          };
          if (next[jur.id].delinquent !== existing.delinquent) changed = true;
        } else {
          // MLC-ERIE-001 (Jun 2026): default the Current-Tax kind OFF when
          // the jurisdiction's current-tax MLC is not enabled / has no fee
          // (effective resolution). Previously current defaulted to true
          // unconditionally, so a jurisdiction whose $ fee lives only on
          // the DELINQUENT collector (City of Erie) tripped the current-tax
          // blocker and froze the whole modal — blocking a perfectly valid
          // delinquent request. Operators can still tick Current manually
          // (and get the specific blocker banner).
          const _curReq = typeof jur?.effective_mlc_required === 'boolean'
            ? jur.effective_mlc_required
            : jur?.mlc_required === true;
          const _curFee = parseFloat(
            jur?.effective_mlc_charges !== undefined && jur?.effective_mlc_charges !== null
              ? jur.effective_mlc_charges
              : jur?.mlc_charges
          );
          const _currentMlcOk = _curReq && !Number.isNaN(_curFee) && _curFee > 0;
          next[jur.id] = {
            current: _currentMlcOk,
            delinquent: !!elig.delinquentEligible,
          };
          changed = true;
        }
      });
      // Drop entries for jurisdictions no longer loaded.
      Object.keys(prev).forEach((k) => {
        if (!next[k]) changed = true;
      });
      return changed ? next : prev;
    });
  }, [jurisdictions, kindEligibility]);

  const toggleKind = useCallback((jurId, kind) => {
    setRequestKindSel((prev) => {
      const cur = prev[jurId] || { current: true, delinquent: false };
      return { ...prev, [jurId]: { ...cur, [kind]: !cur[kind] } };
    });
  }, []);

  // ──────────────────────────────────────────────────────────────────
  // Feb 2026 — Per-request fan-out. Computes the actual list of
  // requests that will be created from the operator's selection +
  // Combine toggle. Each entry represents ONE mail-away letter being
  // produced (one card in the Shipping section, one row in the
  // pre-submit summary, and one downstream PDF / DB row).
  //
  //   key = stable identifier for each request:
  //     • current-tax     → "current:<jurisdictionId>"
  //     • delinquent (single jur or combine OFF)
  //                       → "delinquent:<collectorId>:<jurisdictionId>"
  //     • delinquent (combine ON, ≥2 jurs share bureau)
  //                       → "delinquent:<collectorId>:combined"
  //
  // Pieces consumed by:
  //   • the Shipping-by-Request section (one card per row)
  //   • the validateForm() loop (shipping required per row)
  //   • the submit handler (builds per_request_shipping_overrides)
  //   • the Sending-To banner (one chip per row)
  // ──────────────────────────────────────────────────────────────────
  const pendingRequests = useMemo(() => {
    const rows = [];
    // ──────────────────────────────────────────────────────────────
    // Feb 2026 — Current-Tax Collector Grouping (all-states).
    //
    // Symmetric to the Delinquent grouping below. When ≥ 2 selected
    // jurisdictions share the same `currentCollectorId` (e.g. one
    // municipal collector handles both Borough + School current
    // taxes), we MUST collapse them into a single preview row /
    // shipping card / fee — otherwise the operator sees duplicate
    // "BOROUGH OF COLLINGDALE TAX COLLECTOR" cards and the system
    // appears to charge twice for one envelope. The backend already
    // auto-combines on submit (`current_tax_combine: true` default),
    // so the preview MUST mirror that shape.
    //
    // No state restriction (mirrors the Q1: all-states scope).
    // No operator opt-out toggle wired in this iteration — the
    // backend default-on covers the bug; "Send separately" UI is a
    // P1 follow-up. When that ships, this block reads a state flag
    // and bypasses the bucket loop when False.
    // ──────────────────────────────────────────────────────────────
    const currentBuckets = {};
    // Track collector id per bucket so the outbound row carries the
    // first-seen id (the route uses it to load the bureau record).
    const currentBucketCollectorId = {};
    const currentUngrouped = [];
    jurisdictions.forEach((j) => {
      if (!requestKindSel[j.id]?.current) return;
      const elig = kindEligibility[j.id] || {};
      // Bucket key priority:
      //   1. payee fingerprint (collapses different collector records
      //      that share the same MCPT + address — UAT-PAYEE-GROUPING).
      //   2. raw current collector id (legacy bucket key).
      // Falls through to ungrouped when neither is available.
      const bucketKey =
        elig.currentRecipientFingerprint
        || elig.currentCollectorId
        || null;
      if (bucketKey) {
        (currentBuckets[bucketKey] = currentBuckets[bucketKey] || []).push(j);
        if (!currentBucketCollectorId[bucketKey] && elig.currentCollectorId) {
          currentBucketCollectorId[bucketKey] = elig.currentCollectorId;
        }
      } else {
        currentUngrouped.push(j);
      }
    });
    Object.entries(currentBuckets).forEach(([bucketKey, jurs]) => {
      const elig = kindEligibility[jurs[0].id] || {};
      const cid = currentBucketCollectorId[bucketKey] || elig.currentCollectorId;
      if (jurs.length >= 2) {
        // Combined Current-Tax row — one card, one fee, one envelope.
        rows.push({
          key: `current:${cid}:combined`,
          kind: 'current',
          kindLabel: 'Current-Tax (Combined)',
          jurisdictionIds: jurs.map((x) => x.id),
          primaryJurId: jurs[0].id,
          recipientName: elig.currentRecipientName || jurs[0].name,
          recipientTypeHint:
            jurs[0].type === 'county' ? 'Government' : 'Municipal',
          jurisdictionsLabel: jurs.map((x) => x.name).join(' + '),
          currentCollectorId: cid,
          delinquentCollectorId: null,
        });
      } else {
        // Single-jurisdiction bucket — legacy 1-per-jur row.
        const j = jurs[0];
        const e = kindEligibility[j.id] || {};
        rows.push({
          key: `current:${j.id}`,
          kind: 'current',
          kindLabel: 'Current-Tax',
          jurisdictionIds: [j.id],
          primaryJurId: j.id,
          recipientName: e.currentRecipientName || j.name,
          recipientTypeHint: j.type === 'county' ? 'Government' : 'Municipal',
          jurisdictionsLabel: j.name,
          currentCollectorId: cid,
          delinquentCollectorId: null,
        });
      }
    });
    // No-collector jurisdictions fall through to legacy 1-per-row.
    currentUngrouped.forEach((j) => {
      const elig = kindEligibility[j.id] || {};
      rows.push({
        key: `current:${j.id}`,
        kind: 'current',
        kindLabel: 'Current-Tax',
        jurisdictionIds: [j.id],
        primaryJurId: j.id,
        recipientName: elig.currentRecipientName || j.name,
        recipientTypeHint: j.type === 'county' ? 'Government' : 'Municipal',
        jurisdictionsLabel: j.name,
        currentCollectorId: null,
        delinquentCollectorId: null,
      });
    });
    // Delinquent rows (per collector)
    const delinquentBuckets = {};
    jurisdictions.forEach((j) => {
      if (!requestKindSel[j.id]?.delinquent) return;
      const elig = kindEligibility[j.id] || {};
      if (!elig.delinquentEligible) return;
      const cid = elig.delinquentCollectorId;
      (delinquentBuckets[cid] = delinquentBuckets[cid] || []).push(j);
    });
    Object.entries(delinquentBuckets).forEach(([cid, jurs]) => {
      const elig = kindEligibility[jurs[0].id] || {};
      if (paCombineSelected && jurs.length >= 2) {
        rows.push({
          key: `delinquent:${cid}:combined`,
          kind: 'delinquent',
          kindLabel: 'Delinquent (Combined)',
          jurisdictionIds: jurs.map((x) => x.id),
          primaryJurId: jurs[0].id,
          recipientName: elig.delinquentCollectorName || 'Tax Claim Bureau',
          recipientTypeHint: 'Tax Claim Bureau',
          jurisdictionsLabel: jurs.map((x) => x.name).join(' + '),
          delinquentCollectorId: cid,
        });
      } else {
        jurs.forEach((j) => {
          const e = kindEligibility[j.id] || {};
          rows.push({
            key: `delinquent:${cid}:${j.id}`,
            kind: 'delinquent',
            kindLabel: 'Delinquent',
            jurisdictionIds: [j.id],
            primaryJurId: j.id,
            recipientName: e.delinquentCollectorName || 'Tax Claim Bureau',
            recipientTypeHint: 'Tax Claim Bureau',
            jurisdictionsLabel: j.name,
            delinquentCollectorId: cid,
          });
        });
      }
    });
    return rows;
  }, [jurisdictions, requestKindSel, kindEligibility, paCombineSelected]);

  // Per-request shipping state — keyed by pendingRequests[i].key.
  // Initialized from the originating jurisdiction's shipping fields
  // (so existing per-jurisdiction values seed both legs by default).
  // Operator can then customise each card independently.
  const [requestShipping, setRequestShipping] = useState({});

  useEffect(() => {
    if (pendingRequests.length === 0) {
      setRequestShipping((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      return;
    }
    setRequestShipping((prev) => {
      const next = {};
      let changed = false;
      pendingRequests.forEach((req) => {
        if (prev[req.key]) {
          next[req.key] = prev[req.key];
        } else {
          // Seed from the originating jurisdiction's current shipping
          // values (so the new card starts populated rather than empty).
          const jur = jurisdictions.find((x) => x.id === req.primaryJurId) || {};
          next[req.key] = {
            shipping_method:       jur.shipping_method || '',
            shipping_method_other: jur.shipping_method_other || '',
            shipping_fee:          jur.shipping_fee ?? '',
            sase_required:         (typeof jur.sase_required === 'boolean' ? jur.sase_required : null),
          };
          changed = true;
        }
      });
      // Drop entries for keys that no longer exist.
      Object.keys(prev).forEach((k) => {
        if (!next[k]) changed = true;
      });
      return changed ? next : prev;
    });
  }, [pendingRequests, jurisdictions]);

  const updateRequestShipping = useCallback((key, field, value) => {
    setRequestShipping((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  }, []);

  // (Removed Feb 2026 — `_paInfoNoteText` memo. The unified selection
  // panel below makes the combined-delinquent path discoverable via the
  // checkbox itself, so the legacy info-note is no longer required.)
  const [validationErrors, setValidationErrors] = useState({});
  
  // Client shipping defaults (for fallback)
  const [clientDefaults, setClientDefaults] = useState(null);
  // MLC-TAXHIST-001 — read-only client Tax History requirement (from
  // /shipping-defaults). Display only: the server re-resolves and freezes
  // the snapshot on each current-tax row at /mailaway/send.
  const [taxHistoryRequirement, setTaxHistoryRequirement] = useState(null);
  
  // Expanded state for jurisdiction details
  const [expandedJurisdictions, setExpandedJurisdictions] = useState({});

  // ── Resolved collector defaults map (Feb 2026) ─────────────────────
  // keyed by jurisdiction.id → { mlc_fee, sase_required, eta_days,
  //                              request_instructions, snapshot, source }
  // Master-data driven · read-only on the order · used by the modal to
  // (a) pre-fill `mlc_charges` when only collector_default is set,
  // (b) display provenance subline + Instructions callout,
  // (c) attach snapshot blob to the submit payload.
  const [resolvedDefaults, setResolvedDefaults] = useState({});
  // Feb 2026 — Enrichment phase gate. Prevents the MLC eligibility
  // banner from flashing a stale "MLC not enabled" error during the
  // brief window between the modal's initial fetch (when many rows
  // still have `mlc_required: false`/missing) and the resolver's
  // patch pass (which populates collector-default fees + flips
  // `mlc_required: true`). Phases:
  //   'idle'     — modal closed or no jurisdictions yet
  //   'fetching' — jurisdictions loaded, resolver pass not yet complete
  //   'ready'    — resolver pass complete; blocker UI safe to evaluate
  // Consumed by:
  //   • mlcBlockerNames computation (returns empty during fetching)
  //   • the inline "Checking eligibility…" advisory in place of the
  //     red banner
  //   • the Create button (disabled during fetching as a safety net)
  const [enrichmentPhase, setEnrichmentPhase] = useState('idle');

  // ── SASE tri-state resolution (Aug 2026) ───────────────────────────
  // Effective SASE per request card:
  //   explicit user toggle / jurisdiction override (boolean) → wins
  //   null (inherit) → resolved collector default (resolvedDefaults)
  //   still unknown → false
  const getEffectiveSase = useCallback((req) => {
    const ship = requestShipping[req.key] || {};
    if (typeof ship.sase_required === 'boolean') return ship.sase_required;
    const v = resolvedDefaults?.[req.primaryJurId]?.sase_required?.value;
    return typeof v === 'boolean' ? v : false;
  }, [requestShipping, resolvedDefaults]);

  // MAILAWAY-UI-CLEANUP-003 — when every pending request shares the same
  // carrier + SASE, read-only users get them inline in the summary bar.
  const readOnlyShippingUniform = useMemo(() => {
    if (pendingRequests.length === 0) return null;
    const rows = pendingRequests.map((req) => {
      const ship = requestShipping[req.key] || {};
      return {
        method: ship.shipping_method === 'Other' ? (ship.shipping_method_other || 'Other') : (ship.shipping_method || ''),
        sase: !!getEffectiveSase(req),
        fee: parseFloat(ship.shipping_fee) || 0,
      };
    });
    const first = rows[0];
    const same = rows.every((r) => r.method === first.method && r.sase === first.sase && r.fee === first.fee);
    return same ? first : null;
  }, [pendingRequests, requestShipping, getEffectiveSase]);

  // Stable refs for object props to avoid useEffect re-fire on every parent render
  const certificateDataRef = useRef(certificateData);
  certificateDataRef.current = certificateData;
  const targetJurisdictionRef = useRef(targetJurisdiction);
  targetJurisdictionRef.current = targetJurisdiction;
  const submitGuardRef = useRef(false);

  // Helper to extract jurisdictions from certificateData (real-time form data)
  const extractJurisdictionsFromCertificateData = useCallback((certData, clientShippingDefaults) => {
    if (!certData) return [];
    
    const jurisdictionMap = {};
    
    // Source 1: current_tax_summary (flat structure)
    const taxSummary = certData.current_tax_summary || [];
    taxSummary.forEach((row, idx) => {
      const jurisdictionType = row.jurisdiction_type;
      const jurisdictionName = row.jurisdiction || '';
      
      if (!jurisdictionType && !jurisdictionName) return;
      
      const normalizedName = (jurisdictionName || '').trim().toLowerCase().replace(/\s+/g, '_');
      const uniqueKey = row.jurisdiction_id 
        ? `${jurisdictionType || 'unknown'}:${row.jurisdiction_id}`
        : `${jurisdictionType || 'unknown'}:${normalizedName}`;
      
      if (!jurisdictionMap[uniqueKey]) {
        jurisdictionMap[uniqueKey] = {
          id: row.jurisdiction_id || `cert_${idx}_${Date.now()}`,
          name: jurisdictionName || `${jurisdictionType || 'Unknown'} Jurisdiction`,
          type: jurisdictionType || 'unknown',
          parcel_numbers: [],
          mlc_charges: 0,
          has_request: false,
          request_status: null
        };
      }
      
      // Add parcel APN if available
      const parcelApn = certData.parcel_apns || certData.parcel_apn || '';
      if (parcelApn && !jurisdictionMap[uniqueKey].parcel_numbers.includes(parcelApn)) {
        jurisdictionMap[uniqueKey].parcel_numbers.push(parcelApn);
      }
    });
    
    // Source 2: parcels[].current_tax_summary (multi-parcel structure)
    const parcels = certData.parcels || [];
    parcels.forEach((parcel, parcelIdx) => {
      const parcelApn = parcel.parcel_apn || parcel.apn || `Parcel ${parcelIdx + 1}`;
      const parcelTaxSummary = parcel.current_tax_summary || [];
      
      parcelTaxSummary.forEach((row, rowIdx) => {
        const jurisdictionType = row.jurisdiction_type;
        const jurisdictionName = row.jurisdiction || '';
        
        if (!jurisdictionType && !jurisdictionName) return;
        
        const normalizedName = (jurisdictionName || '').trim().toLowerCase().replace(/\s+/g, '_');
        const uniqueKey = row.jurisdiction_id 
          ? `${jurisdictionType || 'unknown'}:${row.jurisdiction_id}`
          : `${jurisdictionType || 'unknown'}:${normalizedName}`;
        
        if (!jurisdictionMap[uniqueKey]) {
          jurisdictionMap[uniqueKey] = {
            id: row.jurisdiction_id || `cert_parcel${parcelIdx}_row${rowIdx}_${Date.now()}`,
            name: jurisdictionName || `${jurisdictionType || 'Unknown'} Jurisdiction`,
            type: jurisdictionType || 'unknown',
            parcel_numbers: [],
            mlc_charges: 0,
            has_request: false,
            request_status: null
          };
        }
        
        if (parcelApn && !jurisdictionMap[uniqueKey].parcel_numbers.includes(parcelApn)) {
          jurisdictionMap[uniqueKey].parcel_numbers.push(parcelApn);
        }
      });
    });
    
    // Source 3: jurisdictions[] array (new nested structure)
    const jurisdictionsArray = certData.jurisdictions || [];
    jurisdictionsArray.forEach((jur, jurIdx) => {
      const jurisdictionType = jur.jurisdiction_type;
      const jurisdictionName = jur.jurisdiction_name || jur.name || '';
      
      if (!jurisdictionType && !jurisdictionName) return;
      
      const normalizedName = (jurisdictionName || '').trim().toLowerCase().replace(/\s+/g, '_');
      const uniqueKey = jur.jurisdiction_id 
        ? `${jurisdictionType || 'unknown'}:${jur.jurisdiction_id}`
        : `${jurisdictionType || 'unknown'}:${normalizedName}`;
      
      if (!jurisdictionMap[uniqueKey]) {
        jurisdictionMap[uniqueKey] = {
          id: jur.jurisdiction_id || `cert_jur${jurIdx}_${Date.now()}`,
          name: jurisdictionName || `${jurisdictionType || 'Unknown'} Jurisdiction`,
          type: jurisdictionType || 'unknown',
          parcel_numbers: [],
          mlc_charges: 0,
          has_request: false,
          request_status: null
        };
      }
    });
    
    // Source 4: parcels[].jurisdictions[] (JurisdictionManager per-parcel data)
    // This handles the case where jurisdictions are stored per-parcel, not in current_tax_summary
    parcels.forEach((parcel, parcelIdx) => {
      const parcelApn = parcel.parcel_apn || parcel.apn || `Parcel ${parcelIdx + 1}`;
      const parcelJurisdictions = parcel.jurisdictions || [];
      
      parcelJurisdictions.forEach((jur, jurIdx) => {
        const jurisdictionType = jur.jurisdiction_type;
        const jurisdictionName = jur.jurisdiction_name || jur.name || '';
        
        if (!jurisdictionType && !jurisdictionName) return;
        
        const normalizedName = (jurisdictionName || '').trim().toLowerCase().replace(/\s+/g, '_');
        const uniqueKey = jur.jurisdiction_id
          ? `${jurisdictionType || 'unknown'}:${jur.jurisdiction_id}`
          : `${jurisdictionType || 'unknown'}:${normalizedName}`;
        
        if (!jurisdictionMap[uniqueKey]) {
          jurisdictionMap[uniqueKey] = {
            id: jur.jurisdiction_id || `cert_parcel${parcelIdx}_jur${jurIdx}_${Date.now()}`,
            name: jurisdictionName || `${jurisdictionType || 'Unknown'} Jurisdiction`,
            type: jurisdictionType || 'unknown',
            parcel_numbers: [],
            mlc_charges: 0,
            has_request: false,
            request_status: null
          };
        }
        
        if (parcelApn && !jurisdictionMap[uniqueKey].parcel_numbers.includes(parcelApn)) {
          jurisdictionMap[uniqueKey].parcel_numbers.push(parcelApn);
        }
      });
    });
    
    // Convert to array and apply shipping defaults
    return Object.values(jurisdictionMap).map(jur => {
      let shippingMethod = clientShippingDefaults?.shipping_method || '';
      let shippingFee = clientShippingDefaults?.shipping_fee;
      
      return {
        ...jur,
        shipping_method: shippingMethod,
        shipping_method_other: '',
        shipping_fee: shippingFee !== null && shippingFee !== undefined ? String(shippingFee) : '',
        sase_required: null,
        original_shipping_method: shippingMethod,
        original_shipping_fee: shippingFee,
        default_source: clientShippingDefaults?.shipping_method ? 'client' : 'none'
      };
    });
  }, []);

  // Fetch jurisdiction data when modal opens
  useEffect(() => {
    if (!isOpen || !orderData?.id) return;
    
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('token');
        
        // Fetch both mailaway requests and client shipping defaults in parallel
        const [mailawayResponse, shippingResponse] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderData.id}/mailaway/requests`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          ),
          fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderData.id}/shipping-defaults`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          ).catch(() => null) // Don't fail if shipping defaults endpoint fails
        ]);
        
        if (!mailawayResponse.ok) {
          throw new Error('Failed to fetch jurisdiction data');
        }
        
        const mailawayData = await mailawayResponse.json();
        let availableJurisdictions = mailawayData.available_jurisdictions || [];
        
        // Parse client shipping defaults
        let clientShippingDefaults = null;
        if (shippingResponse && shippingResponse.ok) {
          const shippingData = await shippingResponse.json();
          setTaxHistoryRequirement(shippingData.tax_history_requirement || null); // MLC-TAXHIST-001
          if (shippingData.has_defaults) {
            clientShippingDefaults = {
              shipping_method: shippingData.default_shipping_method,
              shipping_fee: shippingData.default_shipping_fee,
              client_name: shippingData.client_name
            };
            setClientDefaults(clientShippingDefaults);
          }
        }
        
        // P0 FIX: If API returns no jurisdictions, fall back to certificateData (real-time form data)
        // This handles the case where user has entered jurisdiction data but hasn't saved yet
        if (availableJurisdictions.length === 0 && certificateDataRef.current) {
          console.log('📬 [MAILAWAY] No jurisdictions from API, extracting from certificateData');
          const extractedJurisdictions = extractJurisdictionsFromCertificateData(certificateDataRef.current, clientShippingDefaults);
          
          if (extractedJurisdictions.length > 0) {
            setJurisdictions(extractedJurisdictions);
            
            // Auto-expand all jurisdictions
            const expanded = {};
            extractedJurisdictions.forEach(j => {
              expanded[j.id] = true;
            });
            setExpandedJurisdictions(expanded);
            setIsLoading(false);
            return;
          }
        }
        
        // Transform jurisdictions with shipping data and SASE
        // Priority: Jurisdiction defaults > Client defaults > Blank
        const transformedJurisdictions = availableJurisdictions.map(jur => {
          // Determine shipping method (priority: jurisdiction > client > blank)
          let shippingMethod = jur.default_shipping_method || '';
          let shippingFee = jur.default_shipping_fee;
          
          // Fallback to client defaults if no jurisdiction defaults
          if (!shippingMethod && clientShippingDefaults?.shipping_method) {
            shippingMethod = clientShippingDefaults.shipping_method;
          }
          if ((shippingFee === null || shippingFee === undefined) && clientShippingDefaults?.shipping_fee != null) {
            shippingFee = clientShippingDefaults?.shipping_fee ?? 0;
          }
          
          return {
            id: jur.id,
            name: jur.name,
            type: jur.type,
            parcel_numbers: jur.parcel_numbers || [],
            mlc_charges: jur.mlc_charges || 0,
            // Mar 2026 — Mail-Away MLC gate. The backend response now
            // carries `mlc_required` from the jurisdiction directory.
            // We propagate it onto each row so validateForm() below can
            // block Create when the fee is missing/zero on a flagged
            // jurisdiction. Defense-in-depth — the server enforces too.
            mlc_required: Boolean(jur.mlc_required),
            // Phase A.7.17 Phase 1 — Carry the collector-scoped MLC
            // enrichment fields produced by the backend
            // `compute_effective_current_collectors` helper. Without
            // this propagation the downstream `_effectiveMlcRequired`
            // / `_effectiveMlcCharges` helpers (and therefore the
            // `mlcBlockerNames` filter) read `undefined` and silently
            // fall back to the per-jurisdiction `mlc_required` /
            // `mlc_charges` flags — re-introducing the very false
            // blocker the Phase 1 backend fix was meant to remove
            // (e.g. Schuylkill County + Blue Mountain SD covered by
            // West Brunswick Township on order TF-20260625-0819).
            // We default `effective_mlc_required` defensively so an
            // older backend (without the enrichment block) still
            // produces a usable boolean.
            effective_mlc_required: (
              typeof jur.effective_mlc_required === 'boolean'
                ? jur.effective_mlc_required
                : Boolean(jur.mlc_required)
            ),
            effective_mlc_charges: (
              jur.effective_mlc_charges !== undefined && jur.effective_mlc_charges !== null
                ? jur.effective_mlc_charges
                : (jur.mlc_charges || 0)
            ),
            effective_collector_id: jur.effective_collector_id || jur.id,
            effective_collector_name: jur.effective_collector_name || jur.name,
            covered_by_jurisdiction_id: jur.covered_by_jurisdiction_id || null,
            covered_by_jurisdiction_name: jur.covered_by_jurisdiction_name || null,
            coverage_match_kind: jur.coverage_match_kind || 'self',
            // Phase A.7.17 Phase 6 — Pre-submit delinquent MLC fee
            // hint. Propagate the backend-resolved delinquent
            // collector identity + directory `default_mlc_fee` so
            // the modal can disable "Create Mail-Away Requests"
            // BEFORE submit when a selected delinquent collector
            // has no fee configured. Mirrors the current-tax
            // `effective_mlc_*` propagation above. Defensive
            // defaults so older backends (without the enrichment)
            // simply skip the pre-submit hint and the backend gate
            // catches it post-submit.
            delinquent_collector_id: jur.delinquent_collector_id || null,
            delinquent_collector_name: jur.delinquent_collector_name || null,
            delinquent_collector_default_mlc_fee: (
              jur.delinquent_collector_default_mlc_fee !== undefined
                ? jur.delinquent_collector_default_mlc_fee
                : null
            ),
            delinquent_collector_fee_ok: (
              typeof jur.delinquent_collector_fee_ok === 'boolean'
                ? jur.delinquent_collector_fee_ok
                : null // null = unknown (older backend) → don't pre-block
            ),
            has_request: jur.has_request || false,
            request_status: jur.request_status,
            // Shipping data per jurisdiction
            shipping_method: shippingMethod,
            shipping_method_other: '',
            shipping_fee: shippingFee !== null && shippingFee !== undefined ? String(shippingFee) : '',
            // SASE per jurisdiction (tri-state: null = inherit collector default)
            sase_required: (typeof jur.sase_required === 'boolean' ? jur.sase_required : null),
            // Track defaults for override detection
            original_shipping_method: shippingMethod,
            original_shipping_fee: shippingFee,
            default_source: jur.default_shipping_method ? 'jurisdiction' : (clientShippingDefaults?.shipping_method ? 'client' : 'none')
          };
        });
        
        // Filter to target jurisdiction if specified (jurisdiction-scoped Mail-Away)
        let finalJurisdictions = transformedJurisdictions;
        if (targetJurisdictionRef.current?.jurisdiction_name) {
          const targetName = targetJurisdictionRef.current.jurisdiction_name.toLowerCase().trim();
          // Phase A.7.17 Phase 11 (Feb 28, 2026 — UAT-COMBINED-COVERAGE-EXPANSION)
          // Phase A.7.17 Phase 12 (Mar 2026 — UAT-COVERED-AUTO-INCLUDE-LEAD)
          // ─────────────────────────────────────────────────────────────
          // When the operator clicks Mail-Away from any row that
          // belongs to a combined-coverage group (the lead OR any
          // covered peer), the modal must expand the filter to
          // include every jurisdiction in that group — so the
          // backend sees the LEAD in the submission and the gate's
          // coverage rescue can validate the covered rows.
          //
          // Bucket key: `covered_by_jurisdiction_id || id`.
          //   • Lead    → covered_by_jurisdiction_id === null → key = lead.id
          //   • Covered → covered_by_jurisdiction_id === lead.id → key = lead.id
          // Both produce the SAME bucket key, so a single filter
          // pulls in every member of the group regardless of which
          // row the operator clicked.
          //
          // Previously we keyed off `effective_collector_id`, but
          // the backend's `compute_effective_current_collectors`
          // emits a DIFFERENT effective_collector_id for the lead
          // (its own jurisdiction id) vs the covered peers (the
          // collector_id from the cert). That mismatch meant
          // clicking a covered row only pulled in other covered
          // peers — silently dropping the lead and tripping the
          // backend MLC gate (UAT-Phase-12 repro).
          const bucketKey = (j) =>
            String(j?.covered_by_jurisdiction_id || j?.id || '');
          const targetJur = transformedJurisdictions.find(
            (j) => (j.name || '').toLowerCase().trim() === targetName
          );
          if (targetJur) {
            const leadBucket = bucketKey(targetJur);
            const expanded = leadBucket
              ? transformedJurisdictions.filter(
                  (j) => bucketKey(j) === leadBucket
                )
              : [targetJur];
            finalJurisdictions = expanded.length > 0 ? expanded : [targetJur];
            if (expanded.length > 1) {
              console.info(
                `[MAILAWAY] Combined coverage expansion — target "${targetName}" `
                + `is lead for ${expanded.length} jurisdictions: `
                + expanded.map((j) => j.name).join(', ')
              );
            }
          } else {
            console.warn('[MAILAWAY] Target jurisdiction not found in available list:', targetName);
          }
        } else if (preselectUnsentOnly) {
          // Feb 2026 — order-level "Send Mail-Away for All" path.
          // Pre-filter to jurisdictions that have NO active current-tax
          // mail-away row already (sent / awaiting_response /
          // response_received). Safer default — avoids duplicate sends
          // for jurisdictions that were already processed. Operator
          // can still see ALL jurisdictions via the per-jurisdiction
          // entry path.
          const sentJurIds = new Set(
            (orderData?.mail_away_requests || [])
              .filter((r) => {
                const kind = r?.request_kind || 'current';
                const status = String(r?.status || '').toLowerCase();
                // Block re-send only when the existing row is the
                // current-tax leg AND still operationally active.
                return kind === 'current'
                  && ['sent', 'awaiting_response', 'response_received'].includes(status);
              })
              .map((r) => r?.jurisdiction_id)
              .filter(Boolean)
              .map(String)
          );
          finalJurisdictions = transformedJurisdictions.filter(
            (j) => !sentJurIds.has(String(j?.jurisdiction_id || j?.id))
          );
          if (finalJurisdictions.length === 0) {
            // Edge case — every jurisdiction has already been sent.
            // Show ALL so the operator at least sees the modal and
            // can manually pick one to re-send via cycle-ack.
            finalJurisdictions = transformedJurisdictions;
            console.info('[MAILAWAY] preselectUnsentOnly: every jurisdiction already sent — showing all');
          }
        }
        
        setJurisdictions(finalJurisdictions);
        // Feb 2026 — Enrichment gate. As soon as jurisdictions are
        // loaded, mark the modal as "fetching" so the MLC blocker UI
        // is suppressed until the resolver pass has had a chance to
        // patch collector-default fees / mlc_required. The resolver
        // effect flips this to 'ready' when its pass completes.
        // Edge case: zero jurisdictions → mark 'ready' immediately so
        // a blank-state modal doesn't spin forever.
        setEnrichmentPhase(finalJurisdictions.length === 0 ? 'ready' : 'fetching');
        
        // Auto-expand all jurisdictions by default
        const expanded = {};
        finalJurisdictions.forEach(j => {
          expanded[j.id] = true;
        });
        setExpandedJurisdictions(expanded);
        
      } catch (err) {
        console.error('Error fetching mailaway data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [isOpen, orderData?.id, preselectUnsentOnly, extractJurisdictionsFromCertificateData]); // certificateData & targetJurisdiction read via refs
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setJurisdictions([]);
      setError(null);
      setValidationErrors({});
      setClientDefaults(null);
      setExpandedJurisdictions({});
      setResolvedDefaults({});
      setEnrichmentPhase('idle');
    }
  }, [isOpen]);

  // Feb 2026 (flash-fix) — Fail-safe timer. If the resolver hangs or
  // never returns (e.g. network error, missing cert data path we
  // didn't anticipate), force enrichmentPhase to 'ready' after a
  // grace period so the modal never gets stuck showing the spinner
  // advisory. 2.5s is long enough for the resolver to win normally
  // and short enough that processors don't notice on a stalled
  // request.
  useEffect(() => {
    if (enrichmentPhase !== 'fetching') return;
    const t = setTimeout(() => {
      setEnrichmentPhase((cur) => (cur === 'fetching' ? 'ready' : cur));
    }, 2500);
    return () => clearTimeout(t);
  }, [enrichmentPhase]);

  // ── Resolver effect (Feb 2026) ──────────────────────────────────────
  // For each jurisdiction in the modal that has a delinquent_collector_id
  // configured on the order, call the master-data resolver and cache the
  // result. If the resolver returns a collector_default fee and the
  // jurisdiction doesn't already have its own mlc_charges, patch the
  // local jurisdiction so the existing blocker logic sees the effective
  // fee. Snapshot is captured into the submit payload at send-time.
  useEffect(() => {
    if (!isOpen || jurisdictions.length === 0) return;
    const certData = certificateDataRef.current;
    if (!certData) {
      // No cert data — resolver can't run. Flip enrichment to 'ready'
      // immediately so the MLC blocker UI evaluates against whatever
      // values came from the initial fetch (instead of hanging in the
      // pending state forever). Same rationale as the fail-safe timer
      // below.
      setEnrichmentPhase('ready');
      return;
    }

    // Build a lookup: jurisdiction_id → collector_id used for the recipient
    // banner / fee resolver call.
    //
    // Feb 2026 — Per-jurisdiction "Mail Away" (single-jurisdiction modal)
    // now generates a Current-Tax MLC routed to the MUNICIPAL collector,
    // not the Tax Claim Bureau. Detect that mode via targetJurisdiction
    // and prefer current_collector_id so the "Sending to" banner and the
    // "Using {collector} default fee" hint reflect the actual recipient.
    //
    // Order-level / multi-jurisdiction modes keep preferring
    // delinquent_collector_id so the combined Tax-Claim-Bureau banner
    // continues to identify the shared bureau (current-tax fan-out is
    // surfaced separately via the pre-submit summary + banner sub-note).
    //
    // Feb 7, 2026 — Option B (Delinquent MLC RCA). Also track WHICH side
    // (delinquent vs current) the chosen collector represents, so the
    // resolver call can suppress the property-jurisdiction override when
    // the collector is the delinquent one. See OperationalDefaultsStrip.jsx
    // file-level header for the full rationale.
    const isSingleJurMode = !!targetJurisdictionRef.current?.jurisdiction_name;
    const collectorByJurId = {};
    const isDelinquentByJurId = {};
    const sources = [
      ...(certData.parcels || []).flatMap((p) => p.jurisdictions || []),
      ...(certData.jurisdictions || []),
    ];
    for (const j of sources) {
      const id = j.jurisdiction_id || j.id;
      if (!id) continue;
      // Single-jurisdiction mode is STRICT: only resolve when an explicit
      // current_collector_id is configured for that jurisdiction. If only
      // the delinquent collector (Tax Claim Bureau) is configured, leave
      // this row UNRESOLVED — the backend's Current-Tax fan-out will
      // address the municipality directly, not the bureau, so we must
      // NOT surface the bureau in the "SENDING TO" banner or the
      // "Using {collector} default fee" hint. The pre-submit summary
      // panel already shows the jurisdiction name as the recipient via
      // its own fallback chain.
      const cid = isSingleJurMode
        ? j.current_collector_id
        : (j.delinquent_collector_id || j.current_collector_id);
      if (cid && !collectorByJurId[id]) {
        collectorByJurId[id] = cid;
        // In single-jur mode the chosen collector is always the current
        // one (delinquent path is suppressed above), so the "is delinquent"
        // flag is always false. Order-level mode keeps the original meaning.
        isDelinquentByJurId[id] = isSingleJurMode
          ? false
          : !!j.delinquent_collector_id;
      }
    }

    let cancelled = false;
    (async () => {
      const next = { ...resolvedDefaults };
      const updates = [];
      for (const jur of jurisdictions) {
        const collectorId = collectorByJurId[jur.id];
        if (!collectorId) continue;
        if (next[jur.id]) continue;        // already resolved
        try {
          // Option B: suppress the property-jurisdiction override when
          // resolving for the *delinquent* collector. The delinquent
          // panel must reflect only the collector's own configuration.
          const isDelinquent = isDelinquentByJurId[jur.id];
          const r = await resolveCollectorDefaults({
            jurisdictionId: isDelinquent ? null : jur.id,
            collectorId,
          });
          if (cancelled) return;
          next[jur.id] = {
            ...r,
            collector_id:     collectorId,
            // Feb 2026 — collector identity for the "Sending to" strip/banner.
            // The resolver endpoint now returns a minimal collector block;
            // we mirror its key fields here so the row/header render code
            // can stay synchronous and read straight from resolvedDefaults.
            _collectorName:   r?.collector?.name || null,
            _collectorType:   r?.collector?.collector_type || null,
            _collectorPhone:  r?.collector?.phone || null,
          };
          updates.push({ id: jur.id, resolved: r });
        } catch (_) { /* swallow — modal still functions with jurisdiction fee only */ }
      }
      if (cancelled) return;
      setResolvedDefaults(next);

      // Patch mlc_charges where collector_default is the source of fee.
      // The existing blocker reads jur.mlc_charges, so this keeps the
      // gate consistent without forking submit logic.
      //
      // Feb 2026 (UAT-MLC-AUTHORITATIVE) — IMPORTANT: we DO NOT touch
      // `mlc_required` here anymore. The jurisdiction directory's
      // `mlc_required` toggle is the authoritative opt-in signal; a
      // collector having a default fee does NOT imply the jurisdiction
      // wants MLC letters. Previously this branch force-flipped
      // `mlc_required: true` whenever the resolver returned a positive
      // fee, which silently bypassed the directory's "MLC Required:
      // OFF" setting and caused unexpected Current-Tax MLC letters
      // (UAT report: Collingdale Boro, Southeast Delco School). Now
      // we only patch the FEE value for display + downstream snapshot
      // capture; the FE/BE eligibility gates honour the directory flag.
      if (updates.length > 0) {
        setJurisdictions((prev) => prev.map((jur) => {
          const u = updates.find((x) => x.id === jur.id);
          if (!u) return jur;
          const feeField = u.resolved?.mlc_fee || {};
          const haveJurFee = parseFloat(jur.mlc_charges) > 0;
          if (!haveJurFee && feeField.value > 0) {
            return {
              ...jur,
              mlc_charges: feeField.value,
              // mlc_required intentionally NOT patched here — keep
              // the value loaded from the directory authoritative.
              _mlc_fee_provenance: feeField.provenance,
              _collector_id: u.resolved.collector_id,
            };
          }
          return {
            ...jur,
            _mlc_fee_provenance: feeField.provenance,
            _collector_id: u.resolved.collector_id,
          };
        }));
      }
      // Feb 2026 — Resolver pass complete (with or without updates).
      // Flip the enrichment gate so the MLC blocker UI can now
      // confidently evaluate against the final mlc_required +
      // mlc_charges values.
      setEnrichmentPhase('ready');
    })();
    return () => { cancelled = true; };
  }, [isOpen, jurisdictions.length]);
  
  // Update shipping method for a specific jurisdiction
  const updateJurisdictionShipping = useCallback((jurisdictionId, field, value) => {
    setJurisdictions(prev => prev.map(jur => {
      if (jur.id !== jurisdictionId) return jur;
      
      const updated = { ...jur, [field]: value };
      
      // Clear "Other" text if not selecting "Other"
      if (field === 'shipping_method' && value !== 'Other') {
        updated.shipping_method_other = '';
      }
      
      return updated;
    }));
    
    // Clear validation error for this field (and general error)
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`${jurisdictionId}_${field}`];
      delete newErrors.general;
      return newErrors;
    });
  }, []);
  
  // Toggle jurisdiction expansion
  const toggleExpanded = useCallback((jurisdictionId) => {
    setExpandedJurisdictions(prev => ({
      ...prev,
      [jurisdictionId]: !prev[jurisdictionId]
    }));
  }, []);
  
  // ──────────────────────────────────────────────────────────────
  // Jun 2026 — UAT-MAILAWAY-MLC-FEE-GROUPING (Phase 19).
  //
  // Production bug: when ≥2 jurisdictions share the same current-tax
  // collector/payee (e.g. one township collects Borough + School +
  // County current taxes), the backend correctly groups them into a
  // SINGLE outgoing MLC request — but this modal's header summary
  // multiplied the MLC fee by jurisdiction count (3 × $60 = $180,
  // 2 × $60 = $120) because it iterated the raw `jurisdictions`
  // array instead of the already-deduplicated `pendingRequests`.
  // Shipping was fine because it already iterated `pendingRequests`
  // (per-envelope) — that asymmetry is the root cause.
  //
  // Fix: iterate `pendingRequests` so the MLC fee is counted ONCE
  // per outgoing request (one per municipal collector/payee). For
  // combined rows the primary jurisdiction's `effective_mlc_charges`
  // is used — same fee the backend applies. Per-parcel multiplier
  // is preserved for current-tax (each parcel adds a fee line on
  // the MLC); delinquent is flat per-shipment.
  //
  // Scope-disciplined: this fix touches ONLY the total-fee
  // calculation. Per-row card displays are unchanged, backend
  // grouping is unchanged, send payload is unchanged.
  // ──────────────────────────────────────────────────────────────
  const totalMlcFee = pendingRequests.reduce((sum, req) => {
    const primaryJur = jurisdictions.find((x) => x.id === req.primaryJurId);
    if (!primaryJur) return sum;
    if (req.kind === 'delinquent') {
      // MLC-ERIE-001: delinquent requests carry the DELINQUENT
      // collector's fee (charged once per request/bureau letter).
      const dFee = parseFloat(
        primaryJur.delinquent_collector_default_mlc_fee
        ?? resolvedDefaults?.[req.primaryJurId]?.mlc_fee?.value
      );
      return sum + (Number.isNaN(dFee) ? 0 : dFee);
    }
    if (req.kind !== 'current') return sum;
    const parcelCount = Math.max(
      (primaryJur.parcel_numbers || []).length || 1,
      1
    );
    const feeRaw = (primaryJur.effective_mlc_charges !== undefined
                    && primaryJur.effective_mlc_charges !== null)
                    ? primaryJur.effective_mlc_charges
                    : primaryJur.mlc_charges;
    const fee = parseFloat(feeRaw) || 0;
    return sum + (fee * parcelCount);
  }, 0);

  // Feb 2026 — Per-request shipping totals + SASE count. Drives the
  // compact header strip so the numbers reflect what's actually being
  // mailed (not whatever happens to be loaded in the jurisdictions
  // array irrespective of selection).
  const totalShippingFee = pendingRequests.reduce((sum, req) => {
    const ship = requestShipping[req.key] || {};
    const fee = parseFloat(ship.shipping_fee) || 0;
    // Parcels only meaningful for Current-Tax (per-parcel pricing).
    // Delinquent letter is one shipment regardless of parcel count.
    const parcelCount = req.kind === 'current'
      ? Math.max(((jurisdictions.find((x) => x.id === req.primaryJurId) || {}).parcel_numbers || []).length || 1, 1)
      : 1;
    return sum + (fee * parcelCount);
  }, 0);

  const saseCount = pendingRequests.filter((req) => getEffectiveSase(req)).length;

  // Feb 2026 — Header counter. "N request(s) across M jurisdiction(s)".
  const distinctJurCount = new Set(
    pendingRequests.flatMap((req) => req.jurisdictionIds)
  ).size;

  // ── "Sending to" recipient summary (Feb 2026) ───────────────────────
  // Operational visibility: surface WHICH collector each Mail-Away is
  // actually being sent to (e.g. MBM Collections for delinquent rows),
  // so processors verify the recipient before submitting.
  //   • If every row resolves to the SAME collector → render a single
  //     top-of-modal banner.
  //   • If rows resolve to DIFFERENT collectors (or any row has no
  //     resolved collector) → render a per-row strip instead.
  // Rows with no resolved collector (e.g. government-only juris without
  // a collector record) are silently skipped — nothing to surface.
  const recipientByJur = jurisdictions
    .map((j) => {
      const r = resolvedDefaults[j.id];
      if (!r || !r._collectorName) return null;
      return {
        jurisdictionId: j.id,
        jurisdictionName: j.name,
        collectorId:    r.collector_id || null,
        collectorName:  r._collectorName,
        collectorType:  r._collectorType,
        collectorPhone: r._collectorPhone,
      };
    });
  const resolvedRecipients = recipientByJur.filter(Boolean);
  const uniqueCollectorIds = Array.from(
    new Set(resolvedRecipients.map((r) => r.collectorId).filter(Boolean))
  );
  // Single banner only when EVERY juris has the same collector AND no
  // juris is missing a recipient. Otherwise per-row strips.
  const singleRecipient =
    uniqueCollectorIds.length === 1 &&
    resolvedRecipients.length === jurisdictions.length
      ? resolvedRecipients[0]
      : null;

  // Feb 2026 — Single-jurisdiction fallback recipient. When the modal is
  // scoped to a single jurisdiction (per-row Mail Away) AND the resolver
  // produced no current-tax collector (e.g. only delinquent is configured
  // on the order), still surface a banner naming the JURISDICTION itself
  // as the recipient. The backend's Current-Tax fan-out addresses the
  // municipality directly in that scenario, so the operator must see
  // "Delaware County" — never the Tax Claim Bureau — in the banner.
  const singleJurFallbackRecipient =
    !singleRecipient
    && !!targetJurisdiction?.jurisdiction_name
    && jurisdictions.length === 1
      ? {
          jurisdictionId: jurisdictions[0].id,
          jurisdictionName: jurisdictions[0].name,
          collectorId:    null,
          collectorName:  jurisdictions[0].name,
          collectorType:  jurisdictions[0].type === 'county' ? 'government' : 'municipal',
          collectorPhone: null,
          _isJurisdictionFallback: true,
        }
      : null;
  const effectiveSingleRecipient = singleRecipient || singleJurFallbackRecipient;

  // ── Mail-Away MLC Eligibility Gate (Mar 2026 — strict policy) ──
  // Mail-Away is permitted ONLY when BOTH conditions hold per row that
  // is being submitted as Current-Tax:
  //   1. `mlc_required === true`   (admin has opted-in)
  //   2. `mlc_charges    >  0`     (a positive fee is configured)
  // Feb 2026 — Restricted to rows where the operator has the Current-
  // Tax checkbox checked. Delinquent-only rows aren't subject to this
  // gate since the delinquent fee comes from the bureau's collector
  // defaults, not the local jurisdiction's MLC fee.
  // Feb 2026 (flash-fix) — Suppressed entirely while
  // `enrichmentPhase !== 'ready'`. During the brief window between
  // the initial fetch and the resolver patch, many rows still carry
  // `mlc_required: false` and would falsely populate the blocker list
  // — producing a red banner that disappears a moment later. The
  // gate now stays silent until the resolver has had a chance to
  // settle, and the UI renders a neutral "Checking eligibility…"
  // advisory in the same slot. See ticket UAT-FLASH-MLC.
  //
  // Phase A.7.17 (UAT-MAILAWAY-CURRENT-MLC-COLLECTOR-SCOPE) —
  // MLC eligibility is COLLECTOR-scoped, not jurisdiction-scoped.
  // The backend `GET /orders/:id/mailaway/requests` attaches
  // `effective_mlc_required` / `effective_mlc_charges` per
  // jurisdiction, resolved via shared `current_collector_id` or
  // matching payee fingerprint on the same order. Both filters
  // below (banner + submit) consume those effective values via the
  // shared `_effectiveMlcRequired` / `_effectiveMlcCharges` helpers.
  // Fallback to the legacy per-jurisdiction values when the
  // enrichment isn't present (pre-redeploy backend, opt-out, or
  // enrichment failure path — see backend best-effort try/except).
  const _effectiveMlcRequired = (jur) =>
    typeof jur?.effective_mlc_required === 'boolean'
      ? jur.effective_mlc_required
      : jur?.mlc_required === true;
  const _effectiveMlcCharges = (jur) =>
    jur?.effective_mlc_charges !== undefined && jur?.effective_mlc_charges !== null
      ? jur.effective_mlc_charges
      : jur?.mlc_charges;
  const mlcBlockerNames = enrichmentPhase !== 'ready'
    ? []
    : jurisdictions
        .filter((jur) => {
          if (!requestKindSel[jur.id]?.current) return false; // not creating current-tax → not blocked
          if (!_effectiveMlcRequired(jur)) return true; // not enabled on the effective collector → blocked
          const num = parseFloat(_effectiveMlcCharges(jur));
          return Number.isNaN(num) || num <= 0; // fee missing/0/<0 on the effective collector → blocked
        })
        .map((jur) => jur.name);
  const hasMlcBlockers = mlcBlockerNames.length > 0;

  // ── Phase A.7.17 Phase 6 (UAT-DELINQUENT-MLC-FEE-PRE-SUBMIT) ──
  // Delinquent collector-level blocker. Groups selected delinquent
  // rows by their `delinquent_collector_id` and surfaces the
  // collector DISPLAY NAME once when its directory
  // `default_mlc_fee` is not configured (`fee_ok === false`). A
  // `null` fee_ok means the older backend didn't enrich the
  // response — we don't block in that case (the server gate still
  // catches it). Mirrors the post-submit error from the
  // `DELINQUENT_MLC_NOT_ELIGIBLE_FOR_MAILAWAY` gate so processors
  // see the same wording before and after submit.
  const delinquentMlcBlockerNames = (() => {
    if (enrichmentPhase !== 'ready') return [];
    const seen = new Set();
    const names = [];
    for (const jur of jurisdictions) {
      if (!requestKindSel[jur.id]?.delinquent) continue;
      if (jur.delinquent_collector_fee_ok === true) continue;
      if (jur.delinquent_collector_fee_ok === null) continue; // unknown → defer
      // fee_ok === false → block
      const key = jur.delinquent_collector_id || jur.delinquent_collector_name || 'unknown';
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(jur.delinquent_collector_name || 'Tax Claim Bureau');
    }
    return names;
  })();
  const hasDelinquentMlcBlockers = delinquentMlcBlockerNames.length > 0;
  const isEnrichmentPending = enrichmentPhase !== 'ready' && jurisdictions.length > 0;
  // Convenience: any submit-blocking validation problem.
  const hasAnyMlcBlockers = hasMlcBlockers || hasDelinquentMlcBlockers;
  
  // Validate form before submission
  const validateForm = () => {
    const errors = {};

    // MLC eligibility (mirrors hasMlcBlockers — same selection-aware filter).
    // Feb 2026 (flash-fix) — Skip during enrichment to avoid asserting
    // a blocker against stale `mlc_required` values from the initial
    // fetch. The submit button is disabled while enrichment is
    // pending so this branch is reachable only post-'ready'.
    //
    // Phase A.7.17 (UAT-MAILAWAY-CURRENT-MLC-COLLECTOR-SCOPE) — same
    // collector-scoped lookup as the banner-side filter. Same
    // helpers, same fallback semantics.
    const mlcBlockers = enrichmentPhase !== 'ready'
      ? []
      : jurisdictions
          .filter((jur) => {
            if (!requestKindSel[jur.id]?.current) return false;
            if (!_effectiveMlcRequired(jur)) return true;
            const num = parseFloat(_effectiveMlcCharges(jur));
            return Number.isNaN(num) || num <= 0;
          })
          .map((jur) => jur.name);
    if (mlcBlockers.length > 0) {
      errors.mlc_blockers = mlcBlockers;
    }

    // Phase A.7.17 Phase 6 — Same selection-aware delinquent
    // collector pre-submit filter as the banner. Surfaces the
    // offending collector display names so the server gate's
    // `DELINQUENT_MLC_NOT_ELIGIBLE_FOR_MAILAWAY` is never
    // reached during normal UI flow (defence-in-depth still
    // applies for API clients that bypass the modal).
    const delinquentCollectorBlockers = (() => {
      if (enrichmentPhase !== 'ready') return [];
      const seen = new Set();
      const out = [];
      for (const jur of jurisdictions) {
        if (!requestKindSel[jur.id]?.delinquent) continue;
        if (jur.delinquent_collector_fee_ok === true) continue;
        if (jur.delinquent_collector_fee_ok === null) continue;
        const key = jur.delinquent_collector_id || jur.delinquent_collector_name || 'unknown';
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(jur.delinquent_collector_name || 'Tax Claim Bureau');
      }
      return out;
    })();
    if (delinquentCollectorBlockers.length > 0) {
      errors.delinquent_mlc_blockers = delinquentCollectorBlockers;
    }

    // Feb 2026 — Validate per-request shipping. Each pendingRequests
    // entry has its own shipping_method / shipping_fee / SASE in
    // `requestShipping[req.key]`. Errors are keyed by `${req.key}_…`
    // so the per-request shipping card highlights its own invalid
    // fields (instead of the legacy jurisdiction-keyed errors).
    pendingRequests.forEach((req) => {
      const ship = requestShipping[req.key] || {};
      if (!ship.shipping_method) {
        errors[`${req.key}_shipping_method`] = 'Shipping method is required';
      }
      if (ship.shipping_method === 'Other' && !ship.shipping_method_other?.trim()) {
        errors[`${req.key}_shipping_method_other`] = 'Please specify the shipping method';
      }
      if (ship.shipping_fee === '' || ship.shipping_fee === null || ship.shipping_fee === undefined) {
        errors[`${req.key}_shipping_fee`] = 'Shipping fee is required';
      } else if (parseFloat(ship.shipping_fee) < 0) {
        errors[`${req.key}_shipping_fee`] = 'Shipping fee must be positive';
      }
    });

    return errors;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Synchronous guard — prevents duplicate submissions before React re-renders
    if (submitGuardRef.current || isSubmitting) return;
    submitGuardRef.current = true;
    
    // Parcel/APN validation before sending Mail-Away
    const parcelCheck = validateParcelsForFax(orderData, certificateData);
    if (!parcelCheck.valid) {
      setValidationErrors({ general: parcelCheck.message });
      submitGuardRef.current = false;
      return;
    }
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      submitGuardRef.current = false;
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Feb 2026 — Unified Mail-Away selection. Build payloads from the
      // per-jurisdiction checkbox state so the operator's choices
      // (Current-Tax / Delinquent / both) become explicit on the wire.
      //
      //   • current_tax_jurisdiction_ids → backend skips Current-Tax
      //     fan-out for any jurisdiction NOT in this list.
      //   • delinquent_groups → one group per selected Tax Claim
      //     Bureau, combined or separate per `paCombineSelected`.
      //   • jurisdiction_ids (existing) → union of jurisdictions with
      //     at least one kind checked, so they remain valid for
      //     delinquent_groups membership server-side.
      const currentTaxJurIds = jurisdictions
        .filter((j) => requestKindSel[j.id]?.current)
        .map((j) => j.id);
      const delinquentJurs = jurisdictions.filter(
        (j) =>
          requestKindSel[j.id]?.delinquent
          && kindEligibility[j.id]?.delinquentEligible
      );
      // Feb 2026 — Explicit Delinquent allow-list, sibling of
      // currentTaxJurIds. Sent verbatim as `delinquent_jurisdiction_ids`
      // so the backend's single-row delinquent fan-out fires ONLY for
      // the jurisdictions the operator opted in to. Without this, the
      // backend default fans out for every eligible jurisdiction in
      // `jurisdiction_ids` and over-creates delinquent rows for
      // current-only selections.
      const delinquentJurIds = delinquentJurs.map((j) => j.id);
      const delinquentBuckets = {};
      delinquentJurs.forEach((j) => {
        const cid = kindEligibility[j.id]?.delinquentCollectorId;
        if (!cid) return;
        (delinquentBuckets[cid] = delinquentBuckets[cid] || []).push(j);
      });
      const delinquentGroups = [];
      Object.entries(delinquentBuckets).forEach(([cid, jurs]) => {
        // Feb 2026 — Combined-delinquent groups must contain ≥ 2
        // jurisdictions (the backend Pydantic model enforces
        // `min_items=2` on `DelinquentGroupPayload.jurisdiction_ids`).
        // Single-jurisdiction buckets are handled by the backend's
        // single-row fan-out via `delinquent_jurisdiction_ids` and
        // do NOT need a delinquent_groups entry. Pushing one would
        // produce: "List should have at least 2 items after
        // validation, not 1".
        if (paCombineSelected && jurs.length >= 2) {
          delinquentGroups.push({
            delinquent_collector_id: cid,
            jurisdiction_ids: jurs.map((j) => j.id),
          });
        }
        // else: single-jurisdiction buckets fall through to the
        // backend's per-jurisdiction fan-out (driven by
        // `delinquent_jurisdiction_ids`). No payload needed here.
      });

      // Filter jurisdiction_shipping + jurisdiction_ids to the union of
      // jurisdictions with ANY kind selected. Keeps the request lean
      // and prevents the backend from validating jurisdictions the
      // operator chose not to send anything for.
      const selectedJurs = jurisdictions.filter(
        (j) => requestKindSel[j.id]?.current || requestKindSel[j.id]?.delinquent
      );
      if (selectedJurs.length === 0) {
        setValidationErrors({ general: 'Select at least one Mail-Away request to create.' });
        submitGuardRef.current = false;
        setIsSubmitting(false);
        return;
      }

      // Build per-jurisdiction shipping data (back-compat fallback for
      // backend's legacy lookup chain). Sources from the FIRST
      // pendingRequests entry that touches each jurisdiction so we
      // always carry some value into `jurisdiction_shipping` even when
      // the operator has only delinquent selected.
      const _shipByJur = {};
      pendingRequests.forEach((req) => {
        const ship = requestShipping[req.key] || {};
        req.jurisdictionIds.forEach((jid) => {
          if (!_shipByJur[jid]) {
            _shipByJur[jid] = {
              shipping_method: ship.shipping_method === 'Other'
                ? (ship.shipping_method_other || 'Other')
                : (ship.shipping_method || ''),
              shipping_fee:    parseFloat(ship.shipping_fee) || 0,
              sase_required:   getEffectiveSase(req),
            };
          }
        });
      });
      const jurisdictionShipping = selectedJurs.map((jur) => {
        const parcelCount = (jur.parcel_numbers || []).length || 1;
        const sj = _shipByJur[jur.id] || {};
        return {
          jurisdiction_id: jur.id,
          jurisdiction_name: jur.name,
          jurisdiction_type: jur.type,
          shipping_method: sj.shipping_method || '',
          shipping_fee: sj.shipping_fee || 0,
          shipping_fee_per_parcel: sj.shipping_fee || 0,
          shipping_total: (sj.shipping_fee || 0) * parcelCount,
          parcel_count: parcelCount,
          sase_required: !!sj.sase_required,
        };
      });

      // Feb 2026 — Per-request shipping overrides. One entry per
      // pendingRequests row, so the backend can apply DIFFERENT
      // shipping for the Current-Tax vs Delinquent leg of the same
      // jurisdiction (the legacy `jurisdiction_shipping` table can
      // only carry one config per jurisdiction).
      const perRequestShippingOverrides = pendingRequests.map((req) => {
        const ship = requestShipping[req.key] || {};
        const resolvedMethod = ship.shipping_method === 'Other'
          ? (ship.shipping_method_other || 'Other')
          : (ship.shipping_method || null);
        return {
          request_kind: req.kind,
          jurisdiction_id: req.kind === 'current' ? req.primaryJurId : null,
          delinquent_collector_id: req.kind === 'delinquent' ? req.delinquentCollectorId : null,
          shipping_method: resolvedMethod,
          shipping_fee: ship.shipping_fee !== undefined && ship.shipping_fee !== ''
            ? parseFloat(ship.shipping_fee) || 0
            : null,
          sase_required: getEffectiveSase(req),
        };
      });

      // Calculate total fee (Current-Tax MLC fees only — delinquent
      // billing flows through the collector defaults snapshot path).
      const totalFee = selectedJurs.reduce((sum, jur) => {
        if (!requestKindSel[jur.id]?.current) return sum;
        const parcelCount = (jur.parcel_numbers || []).length || 1;
        return sum + ((parseFloat(jur.mlc_charges) || 0) * parcelCount);
      }, 0);

      // Feb 2026 — Build immutable collector_defaults_snapshot per
      // jurisdiction. Server persists this verbatim on mail_away_requests
      // so historical billing/audit reports remain stable even if MBM
      // (or any collector) changes its default fee later.
      const collectorDefaultsSnapshot = selectedJurs.reduce((acc, jur) => {
        const resolved = resolvedDefaults[jur.id];
        if (!resolved) return acc;
        acc[jur.id] = {
          collector_id:         resolved.collector_id || null,
          mlc_fee:              resolved.mlc_fee?.value ?? null,
          mlc_fee_source:       resolved.mlc_fee?.provenance ?? 'unset',
          sase_required:        resolved.sase_required?.value ?? null,
          sase_required_source: resolved.sase_required?.provenance ?? 'unset',
          eta_days:             resolved.eta_days?.value ?? null,
          eta_days_source:      resolved.eta_days?.provenance ?? 'unset',
          request_instructions: resolved.request_instructions?.value ?? null,
          frozen_at:            new Date().toISOString(),
        };
        return acc;
      }, {});

      await onSubmit({
        tax_office_fee: totalFee,
        jurisdiction_shipping: jurisdictionShipping,
        order_number: orderData?.order_number || '',
        property_address: orderData?.property?.address || orderData?.property_address_full || '',
        borrower_name: orderData?.borrower_name || '',
        parcel_numbers: selectedJurs.flatMap(j => j.parcel_numbers),
        collector_defaults_snapshot: collectorDefaultsSnapshot,
        _selected_jurisdiction_ids: selectedJurs.map((j) => j.id),
        current_tax_jurisdiction_ids: currentTaxJurIds,
        delinquent_jurisdiction_ids: delinquentJurIds,
        delinquent_groups: delinquentGroups.length > 0 ? delinquentGroups : null,
        per_request_shipping_overrides: perRequestShippingOverrides,
      });
      
      onClose();
    } catch (error) {
      console.error('Mail-Away submission failed:', error);
      // Surface server-side errors (especially the MLC_FEE_NOT_CONFIGURED
      // 400 from /mailaway/send) into the modal so a bypassed frontend
      // — or a stale tab where MLC config changed mid-session — still
      // shows the operator a clear blocker instead of silently failing.
      let serverMsg = null;
      const detail = error?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        serverMsg = detail.message;
      } else if (typeof detail === 'string') {
        serverMsg = detail;
      } else if (error?.message) {
        serverMsg = error.message;
      }
      if (serverMsg) {
        setValidationErrors((prev) => ({ ...prev, general: serverMsg }));
      }
    } finally {
      setIsSubmitting(false);
      submitGuardRef.current = false;
    }
  };

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 px-5 py-3.5 border-b border-indigo-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Mail-Away Request</h2>
                <p className="text-xs text-gray-500" data-testid="mailaway-modal-subheading">
                  {targetJurisdiction?.jurisdiction_name
                    ? `Sending for: ${targetJurisdiction.jurisdiction_name}`
                    : 'All jurisdictions on this order'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              data-testid="mailaway-modal-close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-indigo-600">
                <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm font-medium">Loading jurisdiction data...</span>
              </div>
            </div>
          )}
          
          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="text-red-500 w-5 h-5 flex-shrink-0" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          
          {/* General Validation Error (e.g., missing APN) */}
          {validationErrors.general && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3" data-testid="mailaway-validation-error">
              <AlertCircle className="text-amber-500 w-5 h-5 flex-shrink-0" />
              <p className="text-amber-700 text-sm">{validationErrors.general}</p>
            </div>
          )}

          {/* MLC-TAXHIST-001 — client requirement chip (read-only, not overridable) */}
          {!isLoading && taxHistoryRequirement?.letter_text && (
            <div
              className="flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2"
              data-testid="mailaway-tax-history-requirement"
            >
              <CheckSquare className="w-4 h-4 text-sky-700 flex-shrink-0" />
              <span className="text-sm font-semibold text-sky-900" data-testid="mailaway-tax-history-requirement-label">
                {taxHistoryRequirement.label} — Client Requirement
              </span>
            </div>
          )}

          {/* Mail-Away MLC Eligibility Gate (Mar 2026 — strict policy)
              Hard block — names every jurisdiction that is either
              MLC-disabled or has a non-positive MLC fee. The submit
              button below is also disabled. Backend `/mailaway/send`
              enforces the same rule with 400 MLC_NOT_ELIGIBLE_FOR_MAILAWAY,
              so a bypassed frontend cannot create a request either. */}
          {/* Feb 2026 (flash-fix) — Neutral "Checking eligibility…"
              advisory rendered while the resolver pass is still in
              flight, so the slot above the modal body isn't empty
              (which would let the red blocker banner pop in a
              fraction of a second later). Replaced by the red blocker
              banner once enrichment reaches 'ready' AND blockers
              actually exist. */}
          {isEnrichmentPending && (
            <div
              className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-3"
              data-testid="mailaway-enrichment-pending-advisory"
              role="status"
              aria-live="polite"
            >
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
              <p className="text-sm text-gray-600">
                Checking jurisdiction MLC eligibility and collector defaults…
              </p>
            </div>
          )}

          {hasMlcBlockers && (
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
              data-testid="mailaway-mlc-blocker-banner"
              role="alert"
            >
              <AlertCircle className="text-red-500 w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-700 text-sm font-semibold">
                  Current-Tax Mail-Away cannot be created for{' '}
                  {mlcBlockerNames.join(', ')} — the current-tax MLC is not
                  enabled or its fee is not configured.
                </p>
                <p className="text-red-700 text-xs mt-1">
                  Configure the current-tax collector's MLC fee in Jurisdiction
                  Management, or uncheck the Current-Tax request type to proceed
                  with the other requests. Delinquent requests are validated
                  separately against the delinquent collector's fee.
                </p>
              </div>
            </div>
          )}

          {/* Phase A.7.17 Phase 6 — Delinquent-collector pre-submit
              MLC fee blocker banner. Mirrors the current-tax MLC
              banner above but operates at the COLLECTOR (Tax
              Claim Bureau / 3rd-party servicer) level so multiple
              jurisdictions sharing the same delinquent collector
              are surfaced as ONE row. The wording matches the
              `DELINQUENT_MLC_NOT_ELIGIBLE_FOR_MAILAWAY` 400 error
              the backend gate raises post-submit. */}
          {hasDelinquentMlcBlockers && (
            <div
              className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3"
              data-testid="mailaway-delinquent-mlc-blocker-banner"
              role="alert"
            >
              <AlertCircle className="text-red-500 w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-700 text-sm font-semibold">
                  Cannot create Mail-Away Requests
                </p>
                <p className="text-red-700 text-sm mt-1">
                  The following collector{delinquentMlcBlockerNames.length !== 1 ? 's are' : ' is'}{' '}
                  missing required MLC fee configuration:
                </p>
                <ul className="text-red-700 text-sm font-semibold list-disc list-inside mt-1.5 space-y-0.5">
                  {delinquentMlcBlockerNames.map((nm) => (
                    <li key={nm} data-testid={`mailaway-delinquent-blocker-${nm}`}>{nm}</li>
                  ))}
                </ul>
                <p className="text-red-700 text-xs mt-2">
                  Please contact an Administrator or Jurisdiction
                  Management to configure the collector before
                  creating Mail-Away requests.
                </p>
              </div>
            </div>
          )}
          
          {/* Jurisdictions List */}
          {!isLoading && !error && jurisdictions.length > 0 && (
            <>
              {/* Compact Fee Summary — MAILAWAY-UI-CLEANUP-005: one aligned row
                  "N Request(s) | N Jurisdiction(s) | MLC $ | Shipping $ | SASE | Carrier" */}
              <div
                className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center flex-wrap gap-x-1.5 gap-y-1 text-xs sm:text-[13px] whitespace-nowrap"
                data-testid="mailaway-header-summary"
              >
                <span className="font-semibold text-gray-900" data-testid="mailaway-header-counter">
                  {pendingRequests.length > 0
                    ? `${pendingRequests.length} Request${pendingRequests.length !== 1 ? 's' : ''}`
                    : 'No requests'}
                </span>
                <span className="text-gray-300">|</span>
                <span className="font-semibold text-gray-900">
                  {(pendingRequests.length > 0 ? distinctJurCount : jurisdictions.length)} Jurisdiction{(pendingRequests.length > 0 ? distinctJurCount : jurisdictions.length) !== 1 ? 's' : ''}
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-gray-500">MLC <strong className="text-gray-900 tabular-nums">${totalMlcFee.toFixed(2)}</strong></span>
                <span className="text-gray-300">|</span>
                <span className="text-gray-500">Shipping <strong className="text-gray-900 tabular-nums">${totalShippingFee.toFixed(2)}</strong></span>
                {/* MAILAWAY-UI-CLEANUP-003 — read-only users see SASE + carrier inline
                    (all requests share one value) instead of the expandable cards. */}
                {isLocked && pendingRequests.length > 0 && readOnlyShippingUniform ? (
                  <>
                    <span className="text-gray-300">|</span>
                    <span className="text-gray-500" data-testid="mailaway-header-sase">
                      SASE <strong className={readOnlyShippingUniform.sase ? 'text-amber-700' : 'text-gray-900'}>{readOnlyShippingUniform.sase ? 'Yes' : 'No'}</strong>
                    </span>
                    {readOnlyShippingUniform.method && (
                      <span className="ml-auto text-[11px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold" data-testid="mailaway-header-carrier">
                        {readOnlyShippingUniform.method}
                      </span>
                    )}
                  </>
                ) : (
                  saseCount > 0 && (
                    <>
                      <span className="text-gray-300">|</span>
                      <span className="text-gray-500">SASE <strong className="text-amber-700">{saseCount}</strong></span>
                    </>
                  )
                )}
              </div>

              {/* MAILAWAY-UI-CLEANUP-003 — read-only users, requests with DIFFERENT
                  shipping/SASE/fee values: one compact line per request, no expanders. */}
              {isLocked && pendingRequests.length > 0 && !readOnlyShippingUniform && (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 bg-white" data-testid="mailaway-readonly-shipping-list">
                  {pendingRequests.map((req, index) => {
                    const ship = requestShipping[req.key] || {};
                    const method = ship.shipping_method === 'Other' ? (ship.shipping_method_other || 'Other') : ship.shipping_method;
                    const isCurrent = req.kind === 'current';
                    return (
                      <div key={`ro-${req.key}`} className="px-3 py-2 flex items-center justify-between gap-3 text-xs" data-testid={`mailaway-readonly-shipping-${index}`}>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${isCurrent ? 'bg-emerald-100 text-emerald-800' : 'bg-violet-100 text-violet-800'}`}>{req.kindLabel}</span>
                          <span className="text-gray-400">→</span>
                          <span className="font-medium text-gray-900 truncate">{req.recipientName}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0 text-gray-600">
                          <span>Shipping <strong className="text-gray-900">${(parseFloat(ship.shipping_fee) || 0).toFixed(2)}</strong></span>
                          <span>SASE <strong className={getEffectiveSase(req) ? 'text-amber-700' : 'text-gray-900'}>{getEffectiveSase(req) ? 'Yes' : 'No'}</strong></span>
                          {method && <span className="text-[11px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full font-medium">{method}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ───────────────────────────────────────────────────────
                  Feb 2026 — Unified Mail-Away Request Selection.
                  Operators pick exactly which MLC requests to generate
                  per jurisdiction. Current-Tax is always available;
                  Delinquent (Tax Claim Bureau) is enabled per
                  eligibility (delinquent_collector_id assigned AND
                  same_as_current_payee = false). Ineligible delinquent
                  rows show disabled with a plain-English reason.
                  ─────────────────────────────────────────────────── */}
              {(() => {
                const delinquentCheckedJurs = jurisdictions.filter(
                  (j) => requestKindSel[j.id]?.delinquent
                );
                const collectorGroups = {};
                delinquentCheckedJurs.forEach((j) => {
                  const cid = kindEligibility[j.id]?.delinquentCollectorId;
                  if (!cid) return;
                  (collectorGroups[cid] = collectorGroups[cid] || []).push(j);
                });
                const sharedCollectorCount = Object.values(collectorGroups).filter(
                  (arr) => arr.length >= 2
                ).length;
                const showCombineToggle = sharedCollectorCount > 0;

                return (
                  <div
                    className="rounded-lg border border-indigo-200 bg-white overflow-hidden"
                    data-testid="mailaway-request-selection-panel"
                  >
                    <div className="px-3 py-2.5 bg-indigo-50/60 border-b border-indigo-100 flex items-center gap-2">
                      <CheckSquare className="w-4 h-4 text-indigo-700" />
                      <span className="text-sm font-semibold text-indigo-900">
                        Select Mail-Away Request(s) to Create
                      </span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {jurisdictions.map((jur) => {
                        const sel = requestKindSel[jur.id] || { current: true, delinquent: false };
                        const elig = kindEligibility[jur.id] || {};
                        const currentLabel = `${jur.name} MLC`;
                        const delinquentLabel = `${elig.delinquentCollectorName || 'Tax Claim Bureau'} MLC`;
                        const delinquentSub = `Delinquent tax request sent to ${elig.delinquentCollectorName || 'Tax Claim Bureau'}`;
                        return (
                          <div
                            key={`kind-${jur.id}`}
                            className="px-3 py-2.5"
                            data-testid={`mailaway-kind-row-${jur.id}`}
                          >
                            {jurisdictions.length > 1 && (
                              <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-1.5">
                                {jur.name}
                              </div>
                            )}
                            {/* Current-Tax option — always enabled */}
                            <label
                              className="flex items-start gap-2 cursor-pointer py-1 group"
                              data-testid={`mailaway-kind-current-${jur.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={!!sel.current}
                                onChange={() => toggleKind(jur.id, 'current')}
                                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                data-testid={`mailaway-kind-current-checkbox-${jur.id}`}
                              />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                  {currentLabel}
                                  <span className="ml-1.5 text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                    Current-Tax
                                  </span>
                                </div>
                              </div>
                            </label>
                            {/* Delinquent option — MAILAWAY-UI-CLEANUP-004 (Sep 2026):
                                rendered ONLY when a delinquent collector is configured
                                and applicable (eligibility logic itself is unchanged). */}
                            {elig.delinquentEligible && (
                            <label
                              className="flex items-start gap-2 py-1 group cursor-pointer"
                              data-testid={`mailaway-kind-delinquent-${jur.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={!!sel.delinquent}
                                onChange={() => toggleKind(jur.id, 'delinquent')}
                                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                data-testid={`mailaway-kind-delinquent-checkbox-${jur.id}`}
                              />
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                  {delinquentLabel}
                                  <span className="ml-1.5 text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">
                                    Delinquent
                                  </span>
                                </div>
                                <div className="text-xs text-gray-500">{delinquentSub}</div>
                              </div>
                            </label>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Combine toggle — only shown when ≥2 jurisdictions
                        currently have Delinquent checked AND ≥2 of them
                        share the same Tax Claim Bureau. */}
                    {showCombineToggle && (
                      <div
                        className="px-3 py-2 bg-violet-50/60 border-t border-violet-100"
                        data-testid="mailaway-combine-delinquents-toggle"
                      >
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!paCombineSelected}
                            onChange={() => setPaCombineSelected((v) => !v)}
                            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                            data-testid="mailaway-combine-delinquents-checkbox"
                          />
                          <div>
                            <div className="text-xs font-medium text-violet-900">
                              Combine delinquent letters sharing the same Tax Claim Bureau into one letter
                            </div>
                            <div className="text-[11px] text-violet-700/80">
                              {paCombineSelected
                                ? `${sharedCollectorCount} bureau${sharedCollectorCount !== 1 ? 's' : ''} will receive a single combined letter covering all selected jurisdictions.`
                                : 'Each selected jurisdiction will receive its own separate Delinquent MLC.'}
                            </div>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* MAILAWAY-UI-CLEANUP-005 — Parcel Details replaces the former
                  "Summary of what will be created" panel (counts now live in the
                  header row; the selected requests are listed right above). */}
              {(() => {
                const coveredIds = new Set(pendingRequests.flatMap((r) => r.jurisdictionIds || []));
                const covered = jurisdictions.filter((j) => coveredIds.size === 0 || coveredIds.has(j.id));
                const perJur = covered.map((j) => ({ id: j.id, name: j.name, parcels: Array.from(new Set((j.parcel_numbers || []).filter(Boolean))) }));
                const allParcels = Array.from(new Set(perJur.flatMap((r) => r.parcels)));
                const multi = perJur.length > 1 && perJur.some((r, i, arr) => r.parcels.join('|') !== arr[0].parcels.join('|'));
                return (
                  <div className="bg-blue-50/50 border border-blue-200 rounded-lg" data-testid="mailaway-parcel-details">
                    <div className="px-3 py-2 border-b border-blue-100">
                      <span className="text-sm font-semibold text-blue-900 flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        Parcel Details
                      </span>
                    </div>
                    {pendingRequests.length === 0 && (
                      <div className="px-3 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-100" data-testid="mailaway-pre-submit-summary-blocker">
                        No requests selected. Check at least one box above to create a Mail-Away.
                      </div>
                    )}
                    <div className="px-3 py-2.5">
                      {allParcels.length === 0 ? (
                        <span className="text-xs text-gray-500 italic">No parcel / APN recorded on this certificate.</span>
                      ) : multi ? (
                        <div className="space-y-1.5">
                          {perJur.map((r) => (
                            <div key={`pd-${r.id}`} className="flex items-start gap-2 text-xs" data-testid={`mailaway-parcel-row-${r.id}`}>
                              <span className="text-gray-500 shrink-0 w-40 truncate" title={r.name}>{r.name}</span>
                              <div className="flex flex-wrap gap-1">
                                {r.parcels.length === 0
                                  ? <span className="italic text-gray-400">—</span>
                                  : r.parcels.map((pn) => (
                                    <span key={pn} className="font-mono text-gray-900 bg-white border border-gray-200 rounded px-1.5 py-0.5">{pn}</span>
                                  ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {allParcels.map((pn) => (
                            <span key={pn} className="font-mono text-sm text-gray-900 bg-white border border-gray-200 rounded px-2 py-0.5" data-testid="mailaway-parcel-chip">{pn}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ───────────────────────────────────────────────────────
                  Feb 2026 — Per-Request Shipping Cards.
                  One card per actual request being created. Each card
                  has its own shipping configuration so processors can
                  set different carriers / fees / SASE for the Current-
                  Tax letter vs the Delinquent (Tax Claim Bureau)
                  letter, even when both originate from the same
                  jurisdiction.
                  ─────────────────────────────────────────────────── */}
              {/* MAILAWAY-UI-CLEANUP-003 — editable cards only for users who can
                  actually change shipping/SASE/fee (Admin / Team Leader). */}
              {!isLocked && (
              <div className="space-y-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" />
                  Shipping by Request
                </h3>

                {pendingRequests.length === 0 && (
                  <div
                    className="text-xs text-gray-500 italic border border-dashed border-gray-300 rounded-lg px-3 py-4 text-center"
                    data-testid="mailaway-no-requests-selected"
                  >
                    No requests selected. Use the panel above to pick Current-Tax / Delinquent letters to create.
                  </div>
                )}

                {pendingRequests.map((req, index) => {
                  const ship = requestShipping[req.key] || {};
                  const primaryJur = jurisdictions.find((x) => x.id === req.primaryJurId) || {};
                  const isCurrent = req.kind === 'current';
                  const kindBadgeClass = isCurrent
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-violet-100 text-violet-800';
                  const accentDot = isCurrent ? 'bg-emerald-500' : 'bg-violet-500';
                  const isExpanded = !!expandedJurisdictions[req.key];
                  return (
                    <div
                      key={req.key}
                      className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                      data-testid={`request-card-${index}`}
                    >
                      {/* Request Header */}
                      <div
                        className="bg-gray-50 px-3 py-2.5 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition"
                        onClick={() => toggleExpanded(req.key)}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-1.5 h-1.5 rounded-full ${accentDot} shrink-0`}></div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                              <span className={`text-[10px] uppercase tracking-wide font-bold px-1.5 py-0.5 rounded ${kindBadgeClass}`}>
                                {req.kindLabel}
                              </span>
                              <span className="text-gray-400">→</span>
                              <span className="truncate" data-testid={`request-card-recipient-${index}`}>
                                {req.recipientName}
                              </span>
                              {getEffectiveSase(req) && (
                                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded-full">
                                  <Mail className="w-2.5 h-2.5" />
                                  SASE
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate">
                              {/* MAILAWAY-UI-CLEANUP-002: the covered jurisdiction is only
                                  repeated here for multi-jurisdiction combined rows. */}
                              {!isCurrent && req.jurisdictionIds.length > 1
                                ? `Covering ${req.jurisdictionsLabel} (${req.jurisdictionIds.length} jurisdictions)`
                                : ''}
                              {/* MLC Fee — Feb 2026 (MAILAWAY-FEE-DISPLAY-001).
                                  Show the fee for BOTH Current-Tax and
                                  Delinquent rows. Before the fix only
                                  Current-Tax rows displayed the fee
                                  (via `primaryJur.mlc_charges`),
                                  leaving Delinquent rows blank and
                                  hiding the Tax Claim Bureau charge
                                  from the operator pre-submit.

                                  - Current-Tax: read `primaryJur.mlc_charges`
                                    (sourced from the directory's
                                    current collector fee).
                                  - Delinquent:  read
                                    `resolvedDefaults[primaryJurId].mlc_fee.value`
                                    (the delinquent collector's
                                    operational default — the
                                    resolver loaded the delinquent
                                    collector when the jurisdiction
                                    has a `delinquent_collector_id`).
                              */}
                              {(() => {
                                let _fee = null;
                                if (isCurrent) {
                                  _fee = (primaryJur?.effective_mlc_charges !== undefined
                                          && primaryJur?.effective_mlc_charges !== null)
                                    ? primaryJur.effective_mlc_charges
                                    : primaryJur?.mlc_charges;
                                } else {
                                  // MLC-ERIE-001: the delinquent request's fee
                                  // comes from the DELINQUENT collector record
                                  // (enrichment), independent of current-tax.
                                  _fee = primaryJur?.delinquent_collector_default_mlc_fee
                                    ?? resolvedDefaults?.[req.primaryJurId]?.mlc_fee?.value;
                                }
                                if (_fee == null) return null;
                                const _parsed = parseFloat(_fee);
                                if (isNaN(_parsed) || _parsed <= 0) return null;
                                // MLC-FEE-PER-PARCEL (Jun 2026): current-tax
                                // is per parcel — show the base × parcels
                                // breakdown so the card matches the stored
                                // total, the banner and the PDF.
                                const _pc = isCurrent
                                  ? Math.max((primaryJur?.parcel_numbers || []).length || 1, 1)
                                  : 1;
                                const _lead = (!isCurrent && req.jurisdictionIds.length > 1) ? '· ' : '';
                                return (
                                  <span
                                    className={_lead ? 'ml-2' : ''}
                                    data-testid={`request-card-fee-${index}`}
                                  >
                                    {_lead}MLC Fee: ${_parsed.toFixed(2)}{_pc > 1 ? ` × ${_pc} parcels = $${(_parsed * _pc).toFixed(2)}` : ''}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {ship.shipping_method && (
                            <span className="text-[11px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full">
                              {ship.shipping_method === 'Other' ? (ship.shipping_method_other || 'Other') : ship.shipping_method}
                            </span>
                          )}
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Request Body (Expanded) */}
                      {isExpanded && (
                        <div className="px-3 py-3 space-y-3 border-t border-gray-100">
                          {/* Parcels (from primary jurisdiction) */}
                          {primaryJur.parcel_numbers?.length > 0 && (
                            <div className="text-[11px] text-gray-500">
                              <span className="font-medium">Parcels:</span> {primaryJur.parcel_numbers.join(', ')}
                            </div>
                          )}

                          {/* SASE toggle */}
                          <div className="bg-gray-50 rounded-md px-3 py-2 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <Mail className="w-3.5 h-3.5 text-gray-500" />
                              <span className="text-xs font-medium text-gray-700">SASE Required</span>
                            </div>
                            <label className="inline-flex items-center gap-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={getEffectiveSase(req)}
                                onChange={(e) => updateRequestShipping(req.key, 'sase_required', e.target.checked)}
                                disabled={isLocked}
                                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                data-testid={`request-${index}-sase-required`}
                              />
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                getEffectiveSase(req)
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-gray-200 text-gray-600'
                              }`}>
                                {getEffectiveSase(req) ? 'Yes' : 'No'}
                                {typeof (requestShipping[req.key] || {}).sase_required !== 'boolean' && (
                                  <span className="ml-1 text-[10px] opacity-70">(inherited)</span>
                                )}
                              </span>
                            </label>
                          </div>

                          {/* Shipping Method */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Shipping Method <span className="text-red-500">*</span>
                              {isLocked && (
                                <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded-full">
                                  Locked
                                </span>
                              )}
                            </label>
                            <select
                              value={ship.shipping_method || ''}
                              onChange={(e) => updateRequestShipping(req.key, 'shipping_method', e.target.value)}
                              disabled={isLocked}
                              className={`w-full border rounded-md px-2.5 py-1.5 text-sm appearance-none transition ${
                                validationErrors[`${req.key}_shipping_method`]
                                  ? 'border-red-500 bg-red-50'
                                  : isLocked
                                    ? 'border-gray-200 bg-gray-100 text-gray-600 cursor-not-allowed'
                                    : 'border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500'
                              }`}
                              data-testid={`request-${index}-shipping-method`}
                            >
                              <option value="">-- Select Shipping Method --</option>
                              {SHIPPING_METHODS.map(method => (
                                <option key={method.value} value={method.value}>{method.label}</option>
                              ))}
                            </select>
                            {validationErrors[`${req.key}_shipping_method`] && (
                              <p className="text-red-600 text-[11px] mt-0.5">{validationErrors[`${req.key}_shipping_method`]}</p>
                            )}
                          </div>

                          {/* Other Method Specification */}
                          {ship.shipping_method === 'Other' && (
                            <div>
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Specify Method <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={ship.shipping_method_other || ''}
                                onChange={(e) => updateRequestShipping(req.key, 'shipping_method_other', e.target.value)}
                                disabled={isLocked}
                                placeholder="e.g., FedEx 2Day AM"
                                className={`w-full border rounded-md px-2.5 py-1.5 text-sm transition ${
                                  validationErrors[`${req.key}_shipping_method_other`]
                                    ? 'border-red-500 bg-red-50'
                                    : isLocked
                                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed'
                                      : 'border-gray-300 focus:ring-2 focus:ring-indigo-500'
                                }`}
                                data-testid={`request-${index}-shipping-method-other`}
                              />
                              {validationErrors[`${req.key}_shipping_method_other`] && (
                                <p className="text-red-600 text-[11px] mt-0.5">{validationErrors[`${req.key}_shipping_method_other`]}</p>
                              )}
                            </div>
                          )}

                          {/* Shipping Fee */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Shipping Fee <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <span className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={ship.shipping_fee ?? ''}
                                onChange={(e) => updateRequestShipping(req.key, 'shipping_fee', e.target.value)}
                                disabled={isLocked}
                                placeholder="0.00"
                                className={`w-full pl-6 border rounded-md px-2.5 py-1.5 text-sm transition ${
                                  validationErrors[`${req.key}_shipping_fee`]
                                    ? 'border-red-500 bg-red-50'
                                    : isLocked
                                      ? 'border-gray-200 bg-gray-100 cursor-not-allowed'
                                      : 'border-gray-300 focus:ring-2 focus:ring-indigo-500'
                                }`}
                                data-testid={`request-${index}-shipping-fee`}
                              />
                            </div>
                            {validationErrors[`${req.key}_shipping_fee`] && (
                              <p className="text-red-600 text-[11px] mt-0.5">{validationErrors[`${req.key}_shipping_fee`]}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </>
          )}
          
          {/* Empty State */}
          {!isLoading && !error && jurisdictions.length === 0 && (
            <div className="text-center py-8">
              <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 mb-1">No Jurisdictions Found</h3>
              <p className="text-gray-500 text-xs">This order has no jurisdictions available for Mail-Away.</p>
            </div>
          )}
          </div>
          
          {/* Sticky Footer Actions */}
          {!isLoading && !error && jurisdictions.length > 0 && (
            <div className="flex flex-col gap-0 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              {/* PA Delinquent-Collector Grouping banner removed Feb 2026.
                  The "Combine delinquents sharing the same Tax Claim
                  Bureau" toggle now lives inside the Request Selection
                  panel above, so the operator sees Combine ON/OFF in the
                  same place where they pick which delinquent rows to
                  send. */}

              {/* ─────────────────────────────────────────────────────
                  Feb 2026 P0 — Eligibility ADVISORY message. Renders
                  ONLY when the operator opened the modal scoped to ≥2
                  jurisdictions (i.e. the order-level "Send Mail-Away
                  for All" path) but no shared-Bureau group was found.
                  Walks the loaded jurisdictions and explains in plain
                  English which configuration field is preventing
                  grouping. Non-blocking — operator can still submit.
                  ─────────────────────────────────────────────────── */}
              {!targetJurisdiction
                && jurisdictions.length >= 2
                && (!_paGroups || _paGroups.length === 0)
                && (() => {
                const state = String(orderData?.property_state || orderData?.state || '').toUpperCase();
                if (state !== 'PA') return null;

                // Diagnose what's missing across the loaded jurisdictions.
                const missingDelinq = [];
                const flaggedSame = [];
                const collectorBuckets = {};
                jurisdictions.forEach((j) => {
                  const name = j?.jurisdiction_name || j?.name || '—';
                  const dcId = j?.delinquent_collector_id;
                  const sac = (j?.same_as_current_payee === undefined || j?.same_as_current_payee === null)
                    ? j?.delinquent_same_as_current
                    : j?.same_as_current_payee;
                  if (!dcId) {
                    missingDelinq.push(name);
                    return;
                  }
                  if (sac === true) {
                    flaggedSame.push(name);
                    return;
                  }
                  (collectorBuckets[String(dcId)] = collectorBuckets[String(dcId)] || []).push(name);
                });
                const sharedCount = Object.values(collectorBuckets)
                  .filter((arr) => arr.length >= 2).length;

                // If sharedCount > 0 but _paGroups is empty, the engine
                // saw the bureau match but bailed on `same_as_current_payee`.
                // Otherwise list the actual missing configuration.
                const reasons = [];
                if (missingDelinq.length > 0) {
                  reasons.push(
                    `${missingDelinq.length} of ${jurisdictions.length} jurisdiction(s) have no Delinquent Tax Collector assigned in the Jurisdiction Directory (${missingDelinq.join(', ')}).`
                  );
                }
                if (flaggedSame.length > 0) {
                  reasons.push(
                    `${flaggedSame.length} jurisdiction(s) are flagged "Same as Current Payee" (${flaggedSame.join(', ')}), so there is no separate Tax Claim Bureau to combine on.`
                  );
                }
                if (reasons.length === 0 && sharedCount === 0) {
                  // Every jurisdiction has a delinquent collector and
                  // none share — that's a legitimate "nothing to combine"
                  // state, not a misconfiguration.
                  return null;
                }
                if (reasons.length === 0 && sharedCount > 0) {
                  // Should not happen — keep a safety message.
                  reasons.push(
                    'Grouping eligibility could not be determined. Open each jurisdiction\'s Contact Info → Delinquent Taxes Payable To and confirm the configuration, then re-open this modal.'
                  );
                }

                return (
                  <div
                    className="mx-4 mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900"
                    data-testid="pa-grouping-eligibility-advisory"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-700" />
                      <div className="space-y-1">
                        <div className="font-semibold">
                          {/* Feb 2026 — scoped wording. The Combined
                              CURRENT-TAX grouping (different code path,
                              keyed on `current_collector_id` + payee
                              fingerprint) may be active even when this
                              banner shows, so we no longer claim
                              "Combined Mail-Away grouping not
                              available" as if all combining is off.
                              When current-tax combining is in fact
                              firing on this order, we lead with a
                              positive confirmation. */}
                          {pendingRequests.some(
                            (r) => r.kind === 'current'
                              && (r.jurisdictionIds || []).length >= 2,
                          )
                            ? 'Combined Current-Tax grouping is active — Combined Delinquent grouping is not available for this order'
                            : 'Combined Delinquent grouping is not available for this order'}
                        </div>
                        <ul className="list-disc ml-4 space-y-0.5">
                          {reasons.map((r, idx) => (
                            <li key={idx}>{r}</li>
                          ))}
                        </ul>
                        <div className="text-amber-800/80 mt-1">
                          {/* Feb 2026 — narrowed to delinquent. The
                              "you can still send Current-Tax MLCs" line
                              was technically true but read as if the
                              grouping was OFF; with Combined Current-
                              Tax now landing in this modal, that line
                              actively contradicts what the operator
                              just saw. We now describe ONLY what's
                              missing (the delinquent side) and how to
                              fix it. */}
                          To enable combined-delinquent grouping, open the
                          Tax Certificate panel → each jurisdiction →
                          Contact Info → Delinquent Taxes Payable To. Set
                          the Tax Claim Bureau on every jurisdiction that
                          shares one. The Jurisdiction Directory is read
                          automatically — you do not need to re-enter on
                          each order if the directory already has the
                          assignment.
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* `_paInfoNoteText` render block removed Feb 2026 —
                  superseded by the unified Request Selection panel,
                  which exposes the combined-delinquent option inline. */}
              <div className="flex items-center justify-end gap-2 px-4 py-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={isSubmitting}
                  data-testid="mailaway-modal-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || hasAnyMlcBlockers || isEnrichmentPending}
                  isLoading={isSubmitting}
                  title={
                    isEnrichmentPending
                      ? 'Checking jurisdiction eligibility — please wait a moment before creating the request.'
                      : hasMlcBlockers
                        ? 'Mail-Away cannot be created because MLC is not enabled or MLC Fee is not configured for one or more jurisdictions. Resolve the banner above before creating the request.'
                        : hasDelinquentMlcBlockers
                          ? `Mail-Away cannot be created because MLC fee is not configured for ${delinquentMlcBlockerNames.join(', ')}. Resolve the banner above before creating the request.`
                          : undefined
                  }
                  data-testid="mailaway-modal-submit"
                >
                  {isSubmitting ? (
                    'Creating...'
                  ) : (
                    <>
                      <Truck className="w-3.5 h-3.5" />
                      Create Mail-Away Request{jurisdictions.length > 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default MailAwayModal;
